import useSSE from '../useSSE';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import api from '../utils/api';

// ── Constants
const STATUS_OPTIONS = [
  { value:'not_started', label:'Not Started', color:'#8A93A6' },
  { value:'planning',    label:'Planning',    color:'#2563EB' },
  { value:'in_progress', label:'In Progress', color:'#5B5BD6' },
  { value:'at_risk',     label:'At Risk',     color:'#D97706' },
  { value:'on_hold',     label:'On Hold',     color:'#8A93A6' },
  { value:'escalated',   label:'Escalated',   color:'#DC2626' },
  { value:'completed',   label:'Completed',   color:'#16A34A' },
  { value:'cancelled',   label:'❌ Cancelled', color:'#EF4444' },
];
const CANCELLED_COLOR = '#94A3B8';
const PRIORITY_OPTIONS = [
  { value:'urgent', label:'🔴 Urgent', color:'#DC2626' },
  { value:'high',   label:'🟠 High',   color:'#D97706' },
  { value:'normal', label:'🔵 Normal', color:'#2563EB' },
  { value:'low',    label:'⚪ Low',    color:'#8A93A6' },
];
const TYPE_OPTIONS = [
  { value:'task',      label:'📋 Task' },
  { value:'milestone', label:'🏁 Milestone' },
  { value:'meeting',   label:'📅 Meeting' },
];
const STATUS_COLORS  = Object.fromEntries(STATUS_OPTIONS.map(s=>[s.value,s.color]));
const STATUS_LABELS  = Object.fromEntries(STATUS_OPTIONS.map(s=>[s.value,s.label]));
const PRIORITY_COLORS = Object.fromEntries(PRIORITY_OPTIONS.map(p=>[p.value,p.color]));

// ── ProgressRing — circular progress indicator
function ProgressRing({ value, size=34, isCancelled=false }) {
  const pct = Math.min(100, Math.max(0, value||0));
  const r   = (size-4)/2;
  const circ= 2*Math.PI*r;
  const off = circ-(pct/100)*circ;
  const color = isCancelled ? '#EF4444' : pct>=100?'#16A34A':pct>=75?'#16A34A':pct>=50?'#5B5BD6':pct>=25?'#D97706':'#DC2626';
  return (
    <div style={{position:'relative',width:size,height:size,flexShrink:0,cursor:'pointer'}}>
      <svg width={size} height={size} style={{transform:'rotate(-90deg)'}}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--surface3)" strokeWidth="3"/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={circ} strokeDashoffset={off} strokeLinecap="round"
          style={{transition:'stroke-dashoffset .4s'}}/>
      </svg>
      <span style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*0.22,fontWeight:'700',color}}>{Math.round(pct)}</span>
    </div>
  );
}

// ── Avatar
function Avatar({name,color,size=22}) {
  const i=(name||'?').split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase();
  return <div style={{width:size,height:size,borderRadius:'50%',background:color||'#5B5BD6',display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*.36,fontWeight:'700',color:'#fff',flexShrink:0}}>{i}</div>;
}

// ── Toast
function Toast({toast}) {
  if (!toast) return null;
  const bg = toast.type==='error'?'#DC2626':toast.type==='success'?'#16A34A':'#5B5BD6';
  return (
    <div style={{position:'fixed',bottom:'24px',right:'24px',zIndex:9999,background:bg,color:'#fff',borderRadius:'12px',padding:'14px 20px',boxShadow:'0 8px 32px rgba(0,0,0,0.2)',display:'flex',alignItems:'center',gap:'10px',maxWidth:'400px',fontSize:'13px',fontWeight:'500',animation:'slideUp .2s ease'}}>
      <span style={{fontSize:'16px'}}>{toast.type==='error'?'⚠️':toast.type==='success'?'✅':'ℹ️'}</span>
      <span style={{flex:1}}>{toast.msg}</span>
    </div>
  );
}


// ── Format date DD/MM/YYYY for display
function fmtDate(d) {
  if (!d) return '';
  const [y,m,day] = d.split('-');
  return `${day}/${m}/${y}`;
}

// ── Main ListPage component
export default function ListPage() {
  const { listId } = useParams();
  const navigate   = useNavigate();
  const ctx        = useOutletContext();

  // All state at top — no exceptions
  const [listInfo,        setListInfo]        = useState(null);
  const [myPermission,    setMyPermission]    = useState(null);
  const [tasks,           setTasks]           = useState([]);
  const [users,           setUsers]           = useState([]);
  const [expanded,        setExpanded]        = useState({});
  const [listProgress,    setListProgress]    = useState(0);
  const [editingListName, setEditingListName] = useState(false);
  const [listNameVal,     setListNameVal]     = useState('');
  const [logTimeTask,setLogTimeTask]=useState(null);
  const [pageLoading,     setPageLoading]     = useState(true);
  const [pageError,       setPageError]       = useState(null);
  const [view,            setView]            = useState('list');
  const [editingTask,     setEditingTask]     = useState(null);
  const [showTaskModal,   setShowTaskModal]   = useState(false);
  const [parentForSub,    setParentForSub]    = useState(null);
  const [showComments,    setShowComments]    = useState(null);
  const [comments,        setComments]        = useState([]);
  const [newComment,      setNewComment]      = useState('');
  const [escalationTask,  setEscalationTask]  = useState(null);
  const [escalationOk,    setEscalationOk]   = useState(false);
  const [uncomplete,      setUncomplete]      = useState(null);
  const [deleteConfirm,   setDeleteConfirm]   = useState(null);
  const [weightModal,     setWeightModal]      = useState(null); // {siblings, newTaskName, onConfirm}
  const [toast,           setToast]           = useState(null);
  const [editingCell,     setEditingCell]     = useState(null); // "taskId:field"
  const [siblingLists,    setSiblingLists]    = useState([]);

  // Drag-and-drop refs
  const dragTask         = useRef(null);
  const dragOverTask     = useRef(null);
  const dragSubtask      = useRef(null);
  const dragOverSubtask  = useRef(null);
  const debounceRef      = useRef(null); // debounce rapid SSE events

  useEffect(()=>{ load(); },[listId]);

  // ── Real-time: SSE listener for immediate cross-user updates ──────────────
  const _projectId = listInfo?.project_id;
  useSSE(_projectId, {
    task_updated: (evt) => {
      // Instant in-place progress update from SSE payload (no API call)
      if (evt?.taskId && evt?.listId === listId && evt?.progress !== undefined) {
        setTasks(prev => {
          const updated = prev.map(t => t.id === evt.taskId
            ? { ...t, progress: evt.progress,
                task_weight: evt.task_weight ?? t.task_weight,
                calculatedProgress: evt.calculated_progress ?? evt.progress }
            : t
          );
          const tops = updated.filter(t => !t.parent_task_id);
          let contrib = 0;
          tops.forEach(t => {
            const w = parseFloat(t.task_weight || 0);
            const p = Math.min(100, Math.max(0, parseFloat(t.calculatedProgress ?? t.progress ?? 0)));
            contrib += w * p / 100;
          });



          setListProgress(Math.min(100, Math.max(0, contrib)));
          return updated;
        });
      }
      // Debounced full reload — coalesces rapid events from many users
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        Promise.all([
          api.get(`/lists/${listId}/tasks`).catch(()=>null),
          api.get(`/lists/${listId}`).catch(()=>null),
        ]).then(([tasksRes, infoRes]) => {
          if (tasksRes && Array.isArray(tasksRes)) {
            setTasks(tasksRes);
            const tops = tasksRes.filter(t => !t.parent_task_id);
            let contrib = 0;
            tops.forEach(t => {
              const w = parseFloat(t.task_weight || 0);
              const p = Math.min(100, Math.max(0, parseFloat(t.calculatedProgress ?? t.progress ?? 0)));
              contrib += w * p / 100;
            });
            setListProgress(Math.min(100, Math.max(0, contrib)));
          }
          if (infoRes) setListInfo(infoRes);
        }).catch(() => {});
      }, 400);
    },
    comment_added: (evt) => {
      // Always reload comment counts — never block on silentRef
      api.get(`/lists/${listId}/tasks`).catch(()=>null).then(r => {
        if (r && Array.isArray(r)) setTasks(r);
      });
    },
  })
  // Reload when phase access changes (silent)
  React.useEffect(() => {
    function onAccessChanged() {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        Promise.all([
          api.get(`/lists/${listId}/tasks`).catch(()=>null),
          api.get(`/lists/${listId}`).catch(()=>null),
        ]).then(([tasksRes, infoRes]) => {
          if (tasksRes && Array.isArray(tasksRes)) {
            setTasks(tasksRes);
            const tops = tasksRes.filter(t=>!t.parent_task_id);
            let contrib = 0;
            tops.forEach(t=>{
              const w=parseFloat(t.task_weight||0);
              const p=Math.min(100,Math.max(0,parseFloat(t.calculatedProgress??t.progress??0)));
              contrib+=w*p/100;
            });
            setListProgress(Math.min(100,Math.max(0,contrib)));
          }
          if (infoRes) setListInfo(infoRes);
        }).catch(()=>{});
      }, 300);
    }
    window.addEventListener('trajectory:access_changed', onAccessChanged);
    return () => {
      window.removeEventListener('trajectory:access_changed', onAccessChanged);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [listId]);

  // ── Truly silent real-time sync — merges task data in-place, zero flash
  useEffect(()=>{
    let lastTs = null;
    let active = true;
    let fails = 0;
    const poll = async()=>{
      if(!active||!listId) return;
      const projectId = listInfo?.project_id;
      if(!projectId) return;
      try {
        const r = await api.get(`/spaces/${projectId}/last-updated`).catch(()=>null);
        if(!r){ fails++; if(fails>4) active=false; return; }
        fails=0;
        if(lastTs===null){ lastTs=r.last_updated; return; }
        if(r.last_updated!==lastTs){
          lastTs=r.last_updated;
          // Fetch silently and set state directly — React will only re-render what changed
          Promise.allSettled([
            api.get(`/lists/${listId}/tasks`),
            api.get(`/lists/${listId}`),
          ]).then(([tasksRes, infoRes])=>{
            if(!active) return;
            if(tasksRes.status==='fulfilled' && Array.isArray(tasksRes.value)) {
              setTasks(tasksRes.value);
              const _tops = tasksRes.value.filter(t=>!t.parent_task_id);
              let _contrib = 0;
              _tops.forEach(t => {
                const w = parseFloat(t.task_weight||0);
                const p = Math.min(100,Math.max(0,parseFloat(t.calculatedProgress??t.progress??0)));
                _contrib += w*p/100;
              });
              setListProgress(Math.min(100,Math.max(0,_contrib)));
            }
            if(infoRes.status==='fulfilled' && infoRes.value)
              setListInfo(infoRes.value);
          }).catch(()=>{});
        }
      } catch(_){ fails++; if(fails>4) active=false; }
    };
    const iv = setInterval(poll, 15000);
    return ()=>{ active=false; clearInterval(iv); };
  },[listId, listInfo?.project_id]);
  useEffect(()=>{ if(listInfo?.name) setListNameVal(listInfo.name); },[listInfo?.name]);

  async function saveListName() {
    setEditingListName(false);
    if (!listNameVal.trim() || listNameVal.trim()===listInfo?.name) return;
    try { await api.put('/lists/'+listId,{name:listNameVal.trim()}); load(); } catch(e) {}
  }

  function showToast(msg,type='info') {
    setToast({msg,type});
    setTimeout(()=>setToast(null),4000);
  }

  function showWeightError(total, othersTotal, type='task', siblings=null, newWeight=null, saveDataForModal=null) {
    if (siblings && siblings.length > 0 && newWeight !== null) {
      const isListWeight = saveDataForModal?._isListWeight;
      setWeightModal({
        siblings,
        newWeight,
        newTaskName: isListWeight ? 'this list' : (saveDataForModal?.name || '(new task)'),
        isEdit: true,
        onConfirm: async (adjustedSiblings) => {
          setWeightModal(null);
          if (isListWeight && saveDataForModal?.onAfterConfirm) {
            await saveDataForModal.onAfterConfirm(adjustedSiblings);
          } else {
            for (const s of adjustedSiblings) {
              if (s.newWeight !== s.task_weight) {
                await api.put(`/tasks/${s.id}`, { task_weight: s.newWeight }).catch(()=>{});
              }
            }
            if (saveDataForModal && !isListWeight) await doSaveTask(saveDataForModal);
          }
        }
      });
    } else {
      const max = 100 - othersTotal;
      showToast(
        `⚖️ Weight exceeds 100%! Total would be ${Math.round(total)}%. Other ${type}s use ${Math.round(othersTotal)}%. Max allowed here: ${Math.round(max)}%.`,
        'error'
      );
    }
  }

  async function load() {
    setPageLoading(true); setPageError(null);
    try {
      const [tasksData, listData] = await Promise.all([
        api.get(`/lists/${listId}/tasks`).catch(()=>[]),
        api.get(`/lists/${listId}`).catch(()=>null),
      ]);
      setListInfo(listData);
      // Assignee / PM pickers should only offer people who have access to this
      // project, so we load the project's members rather than every user.
      let usersData = [];
      if (listData?.project_id) {
        try {
          const proj = await api.get(`/projects/${listData.project_id}`);
          usersData = Array.isArray(proj?.members) ? proj.members : [];
        } catch(e) { usersData = []; }
      }
      if (!usersData.length) usersData = await api.get('/users').catch(()=>[]);
      // Load my permission for this project
      if (listData?.project_id) {
        api.get(`/spaces/${listData.project_id}/my-permission`)
          .then(p => { if (p) setMyPermission(p.permission); })
          .catch(() => {});
      }
      // Load sibling lists for weight validation
      if (listData?.phase_id) {
        const sibs = await api.get(`/folders/${listData.phase_id}/lists`).catch(()=>[]);
        setSiblingLists(Array.isArray(sibs)?sibs:[]);
      }
      const tArr = Array.isArray(tasksData)?tasksData:[];
      setTasks(tArr);
      setUsers(Array.isArray(usersData)?usersData:[]);
      // Weighted contribution model: Σ(weight × progress/100)
      // Weight is maximum contribution — no division by total weight
      const top=tArr.filter(t=>!t.parent_task_id);
      let contribution=0;
      top.forEach(t=>{
        const w=t.task_weight||0;
        const p=Math.min(100,Math.max(0,t.calculatedProgress??t.progress??0));
        contribution+=w*p/100;
      });
      setListProgress(Math.min(100,Math.max(0,contribution)));
    } catch(e) {
      setPageError(e?.error||e?.message||'Failed to load');
    } finally {
      setPageLoading(false);
    }
  }

  function openEdit(task,parentId=null) {
    setEditingTask(task); setParentForSub(parentId); setShowTaskModal(true);
  }
  function openNew(parentId=null) {
    setEditingTask(null); setParentForSub(parentId); setShowTaskModal(true);
  }

  // Find a task anywhere in the tree (recursive)
  function findTaskAnywhere(taskList, id) {
    for (const t of taskList) {
      if (t.id === id) return t;
      const found = findTaskAnywhere(t.subtasks || [], id);
      if (found) return found;
    }
    return null;
  }

  // Update comment_count for a task anywhere in the tree (for instant badge update)
  function updateTaskCount(taskList, id, delta) {
    return taskList.map(t => {
      if (t.id === id) return { ...t, comment_count: Math.max(0, (t.comment_count||0) + delta) };
      if (t.subtasks?.length) return { ...t, subtasks: updateTaskCount(t.subtasks, id, delta) };
      return t;
    });
  }

  async function saveTask(data) {
    // Check if adding this task/subtask would cause weight issues
    if (!editingTask?.id) { // only for NEW tasks
      const parentTask = parentForSub ? findTaskAnywhere(tasks, parentForSub) : null;
      const siblings = parentTask
        ? (parentTask.subtasks || [])
        : tasks.filter(t=>!t.parent_task_id);
      const currentTotal = siblings.reduce((s,t)=>s+(t.task_weight!=null?parseFloat(t.task_weight):0),0);
      const newWeight = data.task_weight!=null&&data.task_weight!==''?parseFloat(data.task_weight):0;
      if (currentTotal + newWeight > 100 && siblings.length > 0) {
        // Show weight distribution modal
        setWeightModal({
          siblings,
          newWeight,
          newTaskName: data.name,
          onConfirm: async (adjustedSiblings) => {
            setWeightModal(null);
            // Update sibling weights first
            for (const s of adjustedSiblings) {
              if (s.newWeight !== s.task_weight) {
                await api.put(`/tasks/${s.id}`, { task_weight: s.newWeight }).catch(()=>{});
              }
            }
            // Then save the new task
            await doSaveTask(data);
          }
        });
        return;
      }
    }
    await doSaveTask(data);
  }

  async function doSaveTask(data) {
    try {
      let saved;
      if (editingTask?.id) {
        saved = await api.put(`/tasks/${editingTask.id}`,data);
      } else {
        saved = await api.post(`/lists/${listId}/tasks`,{...data,parent_task_id:parentForSub||undefined});
      }
      setShowTaskModal(false); setEditingTask(null); setParentForSub(null);
      // Reload immediately so time_budget/hours reflect at once
      await load();
    } catch(e) {
      if (e?.error==='Access denied'||e?.status===403) {
        showToast('View-only access — you cannot edit tasks.','error');
        setShowTaskModal(false);
      } else {
        showToast(e?.error||'Failed to save','error');
      }
    }
  }

  function doDelete(id,name) {
    setDeleteConfirm({name,onConfirm:async()=>{
      try { await api.delete(`/tasks/${id}`); load(); }
      catch(e){ showToast(e?.error||'Delete failed','error'); }
    }});
  }

  async function reorderTasks(dragId, overId, parentId) {
    if (!dragId || !overId || dragId === overId) return;
    try {
      if (parentId) {
        const parent = tasks.find(t=>t.id===parentId);
        const subs = [...(parent?.subtasks||[])];
        const fi = subs.findIndex(t=>t.id===dragId);
        const ti = subs.findIndex(t=>t.id===overId);
        if (fi===-1||ti===-1) return;
        subs.splice(ti,0,subs.splice(fi,1)[0]);
        await api.put(`/tasks/${parentId}/reorder-subtasks`,{orderedIds:subs.map(t=>t.id)});
      } else {
        const tops = tasks.filter(t=>!t.parent_task_id);
        const fi = tops.findIndex(t=>t.id===dragId);
        const ti = tops.findIndex(t=>t.id===overId);
        if (fi===-1||ti===-1) return;
        tops.splice(ti,0,tops.splice(fi,1)[0]);
        await api.put(`/lists/${listId}/reorder-tasks`,{orderedIds:tops.map(t=>t.id)});
      }
      load();
    } catch(e) { console.error('Reorder failed',e); }
  }

  function fmtStatusLabel(raw) {
    if (!raw) return '';
    return raw.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase());
  }

  async function exportListCSV() {
    const { downloadStyledWorkbook, prettyLabel } = await import('../utils/excelExport');
    const clean = s => String(s||'').split('\n').join(' ').split('\r').join(' ');
    const columns = [
      { header:'Task Name',    key:'name',      width:40 },
      { header:'Type',         key:'type',      width:14, align:'center' },
      { header:'Status',       key:'status',    width:16, align:'center' },
      { header:'Priority',     key:'priority',  width:12, align:'center' },
      { header:'Assignees',    key:'assignees', width:30 },
      { header:'Progress %',   key:'progress',  width:11, align:'center', numFmt:'0' },
      { header:'Weight %',     key:'weight',    width:11, align:'center', numFmt:'0' },
      { header:'Start Date',   key:'start',     width:13, align:'center' },
      { header:'Due Date',     key:'due',       width:13, align:'center' },
      { header:'Est Hours',    key:'est',       width:11, align:'right', numFmt:'0.00' },
      { header:'Logged Hours', key:'logged',    width:12, align:'right', numFmt:'0.00' },
      { header:'Description',  key:'desc',      width:40 },
      { header:'Parent',       key:'parent',    width:30 },
    ];
    const mkRow = (t, parentName='') => ({
      name:     (parentName ? '    ↳ ' : '') + (t.name||''),
      type:     prettyLabel(t.task_type||'task'),
      status:   prettyLabel(t.status||'not_started'),
      priority: prettyLabel(t.priority||'normal'),
      assignees:(t.assignees||[]).map(a=>a.full_name).join('; '),
      progress: Math.round(parseFloat(t.progress||0)),
      weight:   parseFloat(t.task_weight||0),
      start:    t.start_date||'',
      due:      t.due_date||'',
      est:      parseFloat(t.time_estimate||0),
      logged:   parseFloat(t.time_budget||0),
      desc:     clean(t.description),
      parent:   parentName,
    });
    const rows = [];
    tasks.filter(t=>!t.parent_task_id).forEach(task => {
      rows.push(mkRow(task));
      (task.subtasks||[]).forEach(st => rows.push(mkRow(st, task.name)));
    });
    const nm = (listInfo&&listInfo.name?listInfo.name:'Tasks').replace(/[^a-z0-9]/gi,'_').slice(0,30);
    try {
      await downloadStyledWorkbook(`${nm}_${new Date().toISOString().slice(0,10)}.xlsx`, [{
        name: 'Tasks',
        title: `${listInfo&&listInfo.name?listInfo.name:'Tasks'} — Task Export`,
        subtitle: `Generated ${new Date().toLocaleString('en-GB')} · ${rows.length} task${rows.length!==1?'s':''}`,
        columns,
        rows,
      }]);
    } catch(err) {
      // Fallback: plain CSV (capitalised labels preserved)
      const header = columns.map(c=>c.header);
      const csvRows = [header.map(h=>'"'+h+'"').join(',')];
      rows.forEach(r => {
        csvRows.push(columns.map(c=>'"'+String(r[c.key]??'').split('"').join('""')+'"').join(','));
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob(['\uFEFF'+csvRows.join('\n')],{type:'text/csv'}));
      a.download = nm+'.csv';
      a.click();
    }
  }

  // Inline weight edit from the column view. Mirrors the edit-view behaviour:
  // if raising this task's weight would push its sibling group over 100%, show
  // the Weight Distribution dialog; otherwise save silently.
  function handleWeightEdit(task, rawValue) {
    if (task.status === 'cancelled') {
      showToast('Cancelled tasks have fixed weight (0).','error');
      setEditingCell(null);
      return;
    }
    let newWeight = parseFloat(rawValue);
    if (isNaN(newWeight)) { setEditingCell(null); return; }
    newWeight = Math.max(0, Math.min(100, newWeight));
    const current = parseFloat(task.task_weight ?? 0);
    if (newWeight === current) { setEditingCell(null); return; }

    // Sibling set = tasks sharing the same parent (or top-level siblings), excluding this one.
    const parent = task.parent_task_id ? findTaskAnywhere(tasks, task.parent_task_id) : null;
    const group = parent ? (parent.subtasks || []) : tasks.filter(t => !t.parent_task_id);
    const others = group.filter(t => t.id !== task.id);
    const othersTotal = others.reduce((s,t)=>s + (t.task_weight!=null?parseFloat(t.task_weight):0), 0);

    if (othersTotal + newWeight > 100 && others.length > 0) {
      // Show the same professional Weight Distribution dialog used in edit view.
      setWeightModal({
        siblings: others,
        newWeight,
        newTaskName: task.name,
        onConfirm: async (adjustedSiblings) => {
          setWeightModal(null);
          for (const s of adjustedSiblings) {
            if (s.newWeight !== s.task_weight) {
              await api.put(`/tasks/${s.id}`, { task_weight: s.newWeight }).catch(()=>{});
            }
          }
          await quickUpdate(task.id, 'task_weight', newWeight);
        },
      });
      return;
    }
    // Within budget — save silently.
    quickUpdate(task.id, 'task_weight', newWeight);
  }

  async function quickUpdate(taskId,field,value) {
    if (field==='progress') value = Math.min(100,Math.max(0,parseFloat(value)||0));
    // Cancelled tasks: block manual changes to progress or weight
    const tCheck = findTaskAnywhere(tasks, taskId);
    if (tCheck?.status==='cancelled' && (field==='progress'||field==='task_weight')) {
      showToast('Cancelled tasks have fixed progress (100%) and weight (0).','error');
      return;
    }
    // Block changing status FROM cancelled (only via task modal)
    if (tCheck?.status==='cancelled' && field==='status' && value!=='cancelled') {
      showToast('Open the task to change status from Cancelled.','error');
      return;
    }
    try {
      if (field==='status') {
        const all=[...tasks,...tasks.flatMap(t=>t.subtasks||[])];
        const t=all.find(x=>x.id===taskId);
        if (t?.status==='completed'&&value!=='completed') {
          setUncomplete({taskId,newStatus:value,currentProgress:t.calculatedProgress??t.progress??100});
          return;
        }
        if (value==='escalated') {
          // Show modal FIRST — only save status when user confirms escalation
          // Pass the previous status so we can revert if cancelled
          if (t) { setEscalationTask({...t, _prevStatus: t.status}); return; }
        }
      }
      // Build payload
      let payload = {[field]:value};
      // Always sync actual dates with planned dates
      if (field==='start_date' && value) payload.actual_start_date = value;
      if (field==='due_date' && value) payload.actual_end_date = value;
      await api.put(`/tasks/${taskId}`,payload);
      setEditingCell(null);
      // Update local state immediately for zero-flash experience
      setTasks(prev => {
        function upd(list) {
          return list.map(t => {
            if (t.id === taskId) {
              const u={...t};
              if(field==='progress') u.calculatedProgress=parseFloat(value)||0;
              else if(field==='assignee_ids') u.assignees=(users||[]).filter(x=>Array.isArray(value)&&value.includes(x.id));
              else u[field]=value;
              return u;
            }
            if(t.subtasks?.length) return{...t,subtasks:upd(t.subtasks)};
            return t;
          });
        }
        const newTasks = upd(prev);
        // Recalculate list progress immediately from updated local state
        const tops = newTasks.filter(t=>!t.parent_task_id);
        let contrib = 0;
        tops.forEach(t => {
          const w = parseFloat(t.task_weight||0);
          const p = Math.min(100,Math.max(0,parseFloat(t.calculatedProgress??t.progress??0)));
          contrib += w * p / 100;
        });
        // Use setTimeout to avoid setState during render
        setTimeout(() => setListProgress(Math.min(100,Math.max(0,contrib))), 0);
        return newTasks;
      });
      // Background sync — call load without spinner
      setTimeout(async()=>{
        try {
          const [tasksRes, infoRes] = await Promise.allSettled([
            api.get(`/lists/${listId}/tasks`),
            api.get(`/lists/${listId}`),
          ]);
          if(tasksRes.status==='fulfilled' && Array.isArray(tasksRes.value)){
            const newTasks = tasksRes.value;
            setTasks(newTasks);
            // Recalculate progress from fresh server data
            const tops2 = newTasks.filter(t=>!t.parent_task_id);
            let contrib2 = 0;
            tops2.forEach(t => {
              const w = parseFloat(t.task_weight||0);
              const p = Math.min(100,Math.max(0,parseFloat(t.calculatedProgress??t.progress??0)));
              contrib2 += w * p / 100;
            });
            setListProgress(Math.min(100,Math.max(0,contrib2)));
          }
          if(infoRes.status==='fulfilled' && infoRes.value)
            setListInfo(infoRes.value);
        } catch(_){}
      }, 800);
    } catch(e) {
      if (e?.error==='Access denied') showToast('View-only access.','error');
      else showToast(e?.error||'Update failed','error');
    }
  }

  async function loadComments(taskId) {
    try { setComments(await api.get(`/tasks/${taskId}/comments`)); } catch(e){}
  }

  // Derived
  const topTasks = tasks.filter(t=>!t.parent_task_id);
  const isGuest     = ctx?.isGuest || ctx?.user?.role === 'guest' || myPermission === 'view';
  const isProjectPM = ctx?.isProjectPM || ctx?.user?.role === 'superadmin';
  const pc = listProgress>=100?'#16A34A':listProgress>=50?'#5B5BD6':listProgress>=25?'#D97706':'#DC2626';

  // Columns layout
  const GRID = '28px minmax(240px,3fr) 90px 100px 80px 95px 105px 75px 50px 90px 90px 60px 70px 70px 36px';
  const HEADERS = ['','Task Name','Type','Status','Priority','Assignees','Project Mgr','Weight','Progress','Start Date','Due Date','💬','Budget H','Logged H',''];

  if (pageLoading) return (
    <div className="page-enter" style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:'12px',color:'var(--muted)'}}>
      <div style={{width:20,height:20,border:'2.5px solid var(--border)',borderTopColor:'var(--brand)',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>
      <span>Loading list…</span>
    </div>
  );
  if (pageError) return (
    <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:'16px',padding:'40px',textAlign:'center'}}>
      <div style={{fontSize:'40px'}}>⚠️</div>
      <h3 style={{fontSize:'16px',fontWeight:'700',color:'var(--text)'}}>Could not load list</h3>
      <p style={{fontSize:'13px',color:'var(--muted)'}}>{pageError}</p>
      <div style={{display:'flex',gap:'10px'}}>
        <button className="btn" onClick={()=>navigate(-1)}>← Back</button>
        <button className="btn btn-primary" onClick={load}>Retry</button>
      </div>
    </div>
  );

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',background:'var(--bg)'}}>

      {/* ── HEADER */}
      <div style={{padding:'12px 24px',borderBottom:'1px solid var(--border)',background:'var(--surface)',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:'14px',marginBottom:'8px'}}>
          <div style={{flex:1,minWidth:0}}>
            <h1 style={{fontSize:'17px',fontWeight:'700',color:'var(--text)',margin:0,display:'flex',alignItems:'center',gap:'8px'}}>
              <span style={{color:'var(--muted)',fontWeight:'400',fontSize:'13px'}}>List /</span>
              {editingListName
                ? <input value={listNameVal} onChange={e=>setListNameVal(e.target.value)} autoFocus
                    onBlur={saveListName} onKeyDown={e=>{if(e.key==='Enter')saveListName();if(e.key==='Escape')setEditingListName(false);}}
                    style={{fontSize:'17px',fontWeight:'700',border:'none',borderBottom:'2px solid var(--brand)',outline:'none',background:'transparent',color:'var(--text)',minWidth:'120px',maxWidth:'400px'}}/>
                : <span onDoubleClick={()=>setEditingListName(true)} title="Double-click to rename" style={{cursor:'text'}}>{listInfo?.name||'…'} <span style={{fontSize:'11px',color:'var(--muted)',fontWeight:'400'}}>✏</span></span>
              }
            </h1>
          </div>
          <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
            <div style={{display:'flex',gap:'2px',background:'var(--surface2)',borderRadius:'var(--radius)',padding:'3px',border:'1px solid var(--border)'}}>
              <button className={`btn btn-sm ${view==='list'?'btn-primary':''}`} onClick={()=>setView('list')} style={{fontSize:'12px',padding:'3px 10px'}}>☰ List</button>
              <button className={`btn btn-sm ${view==='board'?'btn-primary':''}`} onClick={()=>setView('board')} style={{fontSize:'12px',padding:'3px 10px'}}>⊞ Board</button>
            </div>
            <button className="btn btn-sm" onClick={exportListCSV} title="Export this list as CSV">⬇ CSV</button>
            <button className="btn btn-sm btn-primary" onClick={()=>openNew(null)}>+ Task</button>
          </div>
        </div>
        {/* Overall progress bar + List weight */}
        <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
          <span style={{fontSize:'11px',color:'var(--muted)',fontWeight:'600',flexShrink:0}}>List Progress</span>
          <div style={{flex:1,height:7,background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
            <div style={{height:'100%',width:`${Math.min(100,listProgress)}%`,background:pc,borderRadius:'99px',transition:'width .5s'}}/>
          </div>
          <span style={{fontSize:'12px',fontWeight:'800',color:pc,minWidth:'34px',flexShrink:0}}>{Math.round(listProgress)}%</span>
          <span style={{fontSize:'11px',color:'var(--muted)',flexShrink:0}}>{topTasks.length} tasks</span>
          {/* List weight — editable */}
          <div style={{display:'flex',alignItems:'center',gap:'5px',padding:'3px 10px',background:'var(--surface2)',borderRadius:'99px',border:'1px solid var(--border)',flexShrink:0}}>
            <span style={{fontSize:'10px',color:'var(--muted)',fontWeight:'600'}}>Weight:</span>
            <input type="number" min="0" max="100" defaultValue={listInfo?.weight||0}
              title="List weight — contribution to phase progress"
              onBlur={async e=>{
                const v=parseFloat(e.target.value)||0;
                const siblings=siblingLists.filter(l=>l.id!==listId);
                const othersTotal=siblings.reduce((s,l)=>s+(l.weight||0),0);
                if(othersTotal+v>100 && siblings.length>0){
                  // Map siblings to task_weight shape so WeightDistributionModal works
                  const siblingsForModal=siblings.map(l=>({...l, task_weight:l.weight||0}));
                  showWeightError(othersTotal+v, othersTotal, 'list', siblingsForModal, v, {
                    _isListWeight: true,
                    listId,
                    newWeight: v,
                    onAfterConfirm: async (adjusted) => {
                      for(const s of adjusted){
                        if(s.newWeight!==(s.weight||0)){
                          await api.put(`/lists/${s.id}`,{weight:s.newWeight}).catch(()=>{});
                          setSiblingLists(prev=>prev.map(l=>l.id===s.id?{...l,weight:s.newWeight}:l));
                        }
                      }
                      await api.put(`/lists/${listId}`,{weight:v});
                      setSiblingLists(prev=>prev.map(l=>l.id===listId?{...l,weight:v}:l));
                    }
                  });
                  e.target.value=listInfo?.weight||0;
                  return;
                }
                try{
                  await api.put(`/lists/${listId}`,{weight:v});
                  setSiblingLists(prev=>prev.map(l=>l.id===listId?{...l,weight:v}:l));
                }catch(err){}
              }}
              onKeyDown={e=>{if(e.key==='Enter')e.target.blur();}}
              style={{width:'42px',height:'22px',border:'1.5px solid var(--brand)',borderRadius:'5px',background:'transparent',color:'var(--brand)',textAlign:'center',fontSize:'12px',fontWeight:'800',outline:'none',padding:'0 2px'}}/>
            <span style={{fontSize:'10px',color:'var(--brand)',fontWeight:'700'}}>%</span>
          </div>
        </div>
      </div>

      {/* ── LIST VIEW */}
      {view==='list' && (
        <div style={{flex:1,overflowY:'auto',overflowX:'auto'}}>
          <div style={{minWidth:'1100px'}}>
            {/* Column headers */}
            <div style={{display:'grid',gridTemplateColumns:GRID,background:'var(--surface2)',borderBottom:'2px solid var(--border)',position:'sticky',top:0,zIndex:5}}>
              {HEADERS.map((h,i)=>(
                <div key={i} style={{padding:'8px 8px',fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted)',borderRight:i<HEADERS.length-1?'1px solid var(--border)':'none',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                  {h}
                </div>
              ))}
            </div>

            {topTasks.length===0 ? (
              <div style={{padding:'60px',textAlign:'center',color:'var(--muted)'}}>
                <div style={{fontSize:'36px',marginBottom:'12px'}}>📋</div>
                <div style={{fontSize:'15px',fontWeight:'600',color:'var(--text)',marginBottom:'6px'}}>No tasks yet</div>
                <div style={{fontSize:'13px',marginBottom:'20px'}}>Click "+ Task" to add your first task</div>
                <button className="btn btn-primary" onClick={()=>openNew(null)}>+ Add First Task</button>
              </div>
            ) : topTasks.map(task=>(
              <TaskRow
                key={task.id} task={task} users={users} level={0}
                GRID={GRID} expanded={expanded} setExpanded={setExpanded}
                editingCell={editingCell} setEditingCell={setEditingCell}
                onEdit={openEdit} onNew={openNew} onDelete={doDelete} onLogTime={task=>setLogTimeTask(task)}
                onQuickUpdate={quickUpdate} onQuickUpdateRaw={quickUpdate}
                onWeightModal={(cfg)=>setWeightModal(cfg)}
                onWeightEdit={handleWeightEdit}
                onComment={(t)=>{setShowComments(t.id);loadComments(t.id);}}
                onReorder={isGuest?null:reorderTasks}
                dragTask={dragTask} dragOverTask={dragOverTask}
                dragSubtask={dragSubtask} dragOverSubtask={dragOverSubtask}
                isGuest={isGuest}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── BOARD VIEW */}
      {view==='board' && (
        <div style={{flex:1,overflowX:'auto',overflowY:'hidden',padding:'16px 20px',display:'flex',gap:'14px',alignItems:'flex-start'}}>
          {[
            {value:'not_started', label:'Not Started', color:'#8A93A6'},
            {value:'planning',    label:'Planning',    color:'#2563EB'},
            {value:'in_progress', label:'In Progress', color:'#5B5BD6'},
            {value:'at_risk',     label:'At Risk',     color:'#D97706'},
            {value:'on_hold',     label:'On Hold',     color:'#94A3B8'},
            {value:'escalated',   label:'Escalated',   color:'#DC2626'},
            {value:'completed',   label:'Completed',   color:'#16A34A'},
            {value:'cancelled',   label:'Cancelled',   color:'#EF4444'},
          ].map(col=>{
            const colTasks=topTasks.filter(t=>t.status===col.value);
            return (
              <div key={col.value} style={{width:'260px',flexShrink:0,background:'var(--surface2)',borderRadius:'12px',border:'1px solid var(--border)',display:'flex',flexDirection:'column',maxHeight:'100%'}}>
                <div style={{padding:'12px 14px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:'8px',flexShrink:0}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:col.color}}/>
                  <span style={{fontSize:'12px',fontWeight:'700',color:'var(--text)',flex:1}}>{col.label}</span>
                  <span style={{fontSize:'11px',color:'var(--muted)'}}>{colTasks.length}</span>
                </div>
                <div style={{padding:'8px',overflowY:'auto',display:'flex',flexDirection:'column',gap:'8px',flex:1}}>
                  {colTasks.map(t=>(
                    <div key={t.id} onClick={()=>openEdit(t)}
                      style={{background:'var(--surface)',borderRadius:'10px',padding:'12px',border:'1px solid var(--border)',cursor:'pointer',transition:'all .15s'}}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor=col.color;e.currentTarget.style.boxShadow=`0 2px 8px ${col.color}20`;}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.boxShadow='none';}}>
                      <div style={{fontSize:'13px',fontWeight:'600',color:t.status==='cancelled'?'#94A3B8':'var(--text)',marginBottom:'8px',textDecoration:t.status==='cancelled'?'line-through':'none'}}>{t.name}</div>
                      <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                        <ProgressRing value={t.calculatedProgress??t.progress??0} size={28}/>
                        <div style={{flex:1,fontSize:'10px',color:'var(--muted)'}}>{t.due_date||'No due date'}</div>
                      </div>
                    </div>
                  ))}
                  <button onClick={()=>{setEditingTask({status:col.value});setParentForSub(null);setShowTaskModal(true);}}
                    style={{background:'none',border:'1.5px dashed var(--border)',borderRadius:'8px',padding:'8px',cursor:'pointer',color:'var(--muted)',fontSize:'12px',transition:'all .15s',width:'100%'}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--brand)';e.currentTarget.style.color='var(--brand)';}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--muted)';}}>
                    + Add Task
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── MODALS */}
      {showTaskModal&&(
        <TaskModal task={editingTask} parentId={parentForSub} users={users} listId={listId}
          projectId={listInfo?.project_id}
          onDelete={editingTask?.id ? doDelete : null}
          onSave={saveTask} onClose={()=>{setShowTaskModal(false);setEditingTask(null);setParentForSub(null);}}
          tasks={tasks} onWeightError={showWeightError}
          depth={parentForSub ? (()=>{
            function getDepth(taskList, id, d=0) {
              for (const t of taskList) {
                if (t.id===id) return d+1;
                const found = getDepth(t.subtasks||[], id, d+1);
                if (found>0) return found;
              }
              return 0;
            }
            return getDepth(tasks, parentForSub);
          })() : 0}/>
      )}
      {showComments&&(
        <CommentsModal taskId={showComments} taskName={findTaskAnywhere(tasks,showComments)?.name} comments={comments} newComment={newComment} users={users}
          setNewComment={setNewComment} currentUserId={ctx?.user?.id} currentUserRole={ctx?.user?.role}
          onClose={()=>{setShowComments(null);setComments([]);}}
          onDelete={async(cId)=>{
            await api.delete(`/comments/${cId}`);
            loadComments(showComments);
            // Immediately decrement comment_count in task state
            setTasks(prev => updateTaskCount(prev, showComments, -1));
          }}
          onPost={async()=>{
            if(!newComment.trim())return;
            await api.post(`/tasks/${showComments}/comments`,{content:newComment});
            setNewComment('');
            loadComments(showComments);
            // Immediately increment comment_count in task state
            setTasks(prev => updateTaskCount(prev, showComments, +1));
          }}/>
      )}
      {escalationTask&&(
        <EscalationModal task={escalationTask} users={users}
          onClose={async()=>{
            // Revert status to previous if cancelled (it wasn't saved yet, so just close)
            setEscalationTask(null);
          }}
          onSend={async(data)=>{
            try{
              // Save status to escalated ONLY now that user confirmed
              await api.put(`/tasks/${escalationTask.id}`,{status:'escalated'});
              await api.post(`/tasks/${escalationTask.id}/escalate`,data);
              setEscalationTask(null);setEscalationOk(true);load();
            }
            catch(e){showToast(e?.error||'Failed to send','error');}
          }}/>
      )}
      {escalationOk&&(
        <div className="modal-overlay">
          <div className="modal" style={{width:'400px',textAlign:'center',padding:'36px'}}>
            <div style={{fontSize:'48px',marginBottom:'12px'}}>✅</div>
            <h3 style={{fontSize:'18px',fontWeight:'800',marginBottom:'8px'}}>Escalation Sent</h3>
            <p style={{fontSize:'13px',color:'var(--muted)',lineHeight:1.6,marginBottom:'20px'}}>The escalation notice has been sent successfully to all selected project managers.</p>
            <button className="btn btn-primary" style={{padding:'10px 32px'}} onClick={()=>setEscalationOk(false)}>Done</button>
          </div>
        </div>
      )}
      {uncomplete&&(
        <UnCompleteModal newStatus={uncomplete.newStatus} currentProgress={uncomplete.currentProgress}
          onConfirm={async(ded)=>{
            try{
              const taskId=uncomplete.taskId; const newSt=uncomplete.newStatus;
              const allT=[...tasks,...tasks.flatMap(t=>t.subtasks||[])];
              const tObj=allT.find(x=>x.id===taskId);
              await api.put(`/tasks/${taskId}`,{status:newSt,progress:Math.max(0,uncomplete.currentProgress-ded)});
              setUncomplete(null); load();
              if (newSt==='escalated' && tObj) { showToast('Status updated','info'); setEscalationTask({...tObj, _prevStatus: tObj.status}); }
              else { showToast(`Progress reduced by ${ded}%`,'info'); }
            }catch(e){showToast(e?.error||'Failed','error');}
          }}
          onCancel={()=>setUncomplete(null)}/>
      )}
      {weightModal && (
        <WeightDistributionModal
          siblings={weightModal.siblings}
          newWeight={weightModal.newWeight}
          newTaskName={weightModal.newTaskName}
          onConfirm={weightModal.onConfirm}
          onCancel={()=>setWeightModal(null)}
        />
      )}

      {deleteConfirm&&(
        <div className="modal-overlay">
          <div className="modal" style={{width:'400px',textAlign:'center',padding:'32px'}}>
            <div style={{fontSize:'40px',marginBottom:'12px'}}>🗑</div>
            <h3 style={{fontSize:'16px',fontWeight:'700',marginBottom:'10px'}}>Delete Task</h3>
            <p style={{fontSize:'13px',color:'var(--muted)',lineHeight:1.6,marginBottom:'24px'}}>Delete <strong>"{deleteConfirm.name}"</strong>? This also deletes all subtasks and cannot be undone.</p>
            <div style={{display:'flex',gap:'10px',justifyContent:'center'}}>
              <button className="btn" onClick={()=>setDeleteConfirm(null)}>Cancel</button>
              <button style={{padding:'8px 20px',background:'#DC2626',color:'#fff',border:'none',borderRadius:'var(--radius)',fontWeight:'700',cursor:'pointer',fontSize:'13px'}}
                onClick={()=>{deleteConfirm.onConfirm();setDeleteConfirm(null);}}>Delete</button>
            </div>
          </div>
        </div>
      )}
      <Toast toast={toast}/>
    </div>
  );
}

// ── TaskRow: renders a task + its subtasks recursively
function TaskRow({task,users,level,GRID,expanded,setExpanded,editingCell,setEditingCell,onEdit,onNew,onDelete,onQuickUpdate,onQuickUpdateRaw,onWeightModal,onWeightEdit,onComment,onLogTime,onReorder,dragTask,dragOverTask,dragSubtask,dragOverSubtask,isGuest}) {
  const isOpen   = !!expanded[task.id];
  const subs     = task.subtasks||[];
  const progress = Math.min(100, task.calculatedProgress??task.progress??0);
  const isSub    = level>0;
  const sc       = STATUS_COLORS[task.status]||'#8A93A6';
  const pc2      = {urgent:'#DC2626',high:'#EA580C',normal:'#5B5BD6',low:'#16A34A'}[task.priority||'normal']||'#8A93A6';
  const ec       = k => editingCell===`${task.id}:${k}`;
  const se       = k => setEditingCell(`${task.id}:${k}`);
  const ce       = ()=> setEditingCell(null);



  const isOverdue= task.due_date&&task.due_date<new Date().toISOString().slice(0,10)&&task.status!=='completed';

  function InlineSelect({field,value,options}) {
    if (!ec(field)) return (
      <div onClick={()=>se(field)} style={{cursor:'pointer',minHeight:'22px',display:'flex',alignItems:'center',width:'100%'}}>
        {options.find(o=>o.value===value)?.label || <span style={{color:'var(--muted)'}}>—</span>}
      </div>
    );
    return (
      <select autoFocus className="select" value={value} style={{width:'100%',height:'28px',fontSize:'11px',padding:'0 4px'}}
        onChange={e=>{onQuickUpdate(task.id,field,e.target.value);ce();}}
        onBlur={ce}>
        {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }

  function InlineInput({field,value,type='text'}) {
    if (!ec(field)) return (
      <div onClick={()=>se(field)} style={{cursor:'pointer',minHeight:'22px',display:'flex',alignItems:'center',width:'100%',fontSize:'12px',color:'var(--text)'}}>
        {value||<span style={{color:'var(--muted)'}}>—</span>}
      </div>
    );
    return (
      <input autoFocus type={type} className="input" defaultValue={value||''} style={{width:'100%',height:'28px',fontSize:'11px',padding:'0 6px'}}
        onBlur={e=>{onQuickUpdate(task.id,field,e.target.value);ce();}}
        onKeyDown={e=>{if(e.key==='Enter'){onQuickUpdate(task.id,field,e.target.value);ce();}if(e.key==='Escape')ce();}}/>
    );
  }

  // Assignee display
  const assignees = task.assignees||[];
  const pms = Array.isArray(task.project_managers)
    ? task.project_managers.map(id=>users.find(u=>u.id===id)).filter(Boolean)
    : [];

  function handleDragStart() {
    if (!isSub) { if(dragTask) dragTask.current=task.id; }
    else { if(dragSubtask) dragSubtask.current=task.id; }
  }
  function handleDragEnter() {
    if (!isSub) { if(dragOverTask) dragOverTask.current=task.id; }
    else { if(dragOverSubtask) dragOverSubtask.current=task.id; }
  }
  function handleDragEnd() {
    if (!isSub && dragTask?.current && dragOverTask?.current && dragTask.current!==dragOverTask.current) {
      onReorder&&onReorder(dragTask.current, dragOverTask.current, null);
      dragTask.current=null; dragOverTask.current=null;
    } else if (isSub && dragSubtask?.current && dragOverSubtask?.current && dragSubtask.current!==dragOverSubtask.current) {
      onReorder&&onReorder(dragSubtask.current, dragOverSubtask.current, task.parent_task_id);
      dragSubtask.current=null; dragOverSubtask.current=null;
    }
  }

  return (
    <div>
      {/* ── Row */}
      <div
        draggable={!!onReorder}
        onDragStart={handleDragStart}
        onDragEnter={handleDragEnter}
        onDragEnd={handleDragEnd}
        onDragOver={e=>e.preventDefault()}
        style={{display:'grid',gridTemplateColumns:GRID,borderBottom:'1px solid var(--border)',minHeight:'44px',alignItems:'center',
          background:task.status==='cancelled'?'rgba(148,163,184,0.06)':level===0?'transparent':level===1?'rgba(91,91,214,0.03)':'rgba(110,86,207,0.055)',
          transition:'background .1s',cursor:onReorder?'grab':'default',
          opacity:task.status==='cancelled'?0.7:1,
        }}
        onMouseEnter={e=>e.currentTarget.style.background=task.status==='cancelled'?'rgba(148,163,184,0.1)':level===0?'rgba(91,91,214,0.035)':level===1?'rgba(91,91,214,0.06)':'rgba(110,86,207,0.09)'}
        onMouseLeave={e=>e.currentTarget.style.background=task.status==='cancelled'?'rgba(148,163,184,0.06)':level===0?'transparent':level===1?'rgba(91,91,214,0.03)':'rgba(110,86,207,0.055)'}>

        {/* Expand toggle */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',paddingLeft:4+level*20}}>
          <button onClick={()=>setExpanded(p=>({...p,[task.id]:!p[task.id]}))}
            style={{background:'none',border:'none',cursor:subs.length>0?'pointer':'default',
              color:subs.length>0?(level===0?'#5B5BD6':level===1?'#6E56CF':'#8B5CF6'):'var(--border)',
              fontSize:'11px',padding:'4px',transition:'transform .2s, color .15s',
              transform:isOpen?'rotate(90deg)':'none',
              opacity:subs.length===0?0.3:1,
              lineHeight:1}}>▶</button>
        </div>

        {/* Name */}
        <div style={{padding:'5px 10px',display:'flex',alignItems:'center',gap:'8px',minWidth:0,borderLeft:'1px solid var(--border)'}}>
          {level>0&&<span style={{
            color:level===1?'#6E56CF':'#8B5CF6',
            fontSize:'13px',flexShrink:0,fontWeight:'700',lineHeight:1,
            letterSpacing:level>=2?'-1px':'0',
          }}>{'↳'.repeat(Math.min(level,2))}</span>}
          <span style={{
            fontSize:level===0?'13px':'12.5px',
            fontWeight:level===0?'600':'500',
            fontStyle:'normal',
            color:task.status==='cancelled'?'#94A3B8':level===0?'var(--text)':level===1?'var(--text)':'#3D3D6B',
            overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,cursor:'pointer',
            letterSpacing:level===0?'-0.1px':'0',
            textDecoration:task.status==='cancelled'?'line-through':'none',
          }}
            onClick={()=>onEdit(task)} title={task.name}>{(task.is_major_milestone===1||task.is_major_milestone===true)&&<span title="Major task / milestone" style={{marginRight:'4px'}}>⭐</span>}{task.name}</span>
          {!isGuest&&level<2&&(
            <button
              onClick={e=>{e.stopPropagation();onNew(task.id);}}
              title={level===0?'Add Subtask':'Add Sub-subtask'}
              style={{
                display:'inline-flex',alignItems:'center',justifyContent:'center',
                flexShrink:0,cursor:'pointer',fontSize:'15px',fontWeight:'800',lineHeight:1,
                width:'22px',height:'22px',borderRadius:'50%',
                border:`1.5px solid ${level===0?'#5B5BD6':level===1?'#6E56CF':'#8B5CF6'}`,
                background:`${level===0?'#5B5BD6':level===1?'#6E56CF':'#8B5CF6'}18`,
                color:level===0?'#5B5BD6':level===1?'#6E56CF':'#8B5CF6',
                transition:'all .15s',
              }}
              onMouseEnter={e=>{e.currentTarget.style.background=level===0?'#5B5BD6':level===1?'#6E56CF':'#8B5CF6';e.currentTarget.style.color='#fff';}}
              onMouseLeave={e=>{e.currentTarget.style.background=`${level===0?'#5B5BD6':level===1?'#6E56CF':'#8B5CF6'}18`;e.currentTarget.style.color=level===0?'#5B5BD6':level===1?'#6E56CF':'#8B5CF6';}}>
              +
            </button>
          )}
        </div>

        {/* Type */}
        <div style={{padding:'5px 8px',borderLeft:'1px solid var(--border)'}}>
          <InlineSelect field="task_type" value={task.task_type||'task'}
            options={TYPE_OPTIONS.map(o=>({value:o.value,label:o.label.split(' ')[1]}))}/>
        </div>

        {/* Status */}
        <div style={{padding:'5px 8px',borderLeft:'1px solid var(--border)'}}>
          {ec('status')
            ? <select autoFocus className="select" value={task.status} style={{width:'100%',height:'28px',fontSize:'11px',padding:'0 4px'}}
                onChange={e=>{onQuickUpdate(task.id,'status',e.target.value);ce();}} onBlur={ce}>
                {STATUS_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            : <span onClick={()=>se('status')} style={{fontSize:'10px',fontWeight:'700',padding:'2px 8px',borderRadius:'99px',background:`${sc}18`,color:sc,border:`1px solid ${sc}30`,cursor:'pointer',whiteSpace:'nowrap',display:'inline-block'}}>
                {STATUS_LABELS[task.status]||task.status}
              </span>
          }
        </div>

        {/* Priority */}
        <div style={{padding:'5px 8px',borderLeft:'1px solid var(--border)'}}>
          <InlineSelect field="priority" value={task.priority||'normal'}
            options={PRIORITY_OPTIONS.map(o=>({value:o.value,label:o.label}))}/>
        </div>

        {/* Assignees — inline multi-select */}
        <div style={{padding:'5px 6px',borderLeft:'1px solid var(--border)',position:'relative'}}>
          {ec('assignees')
            ? <AssigneePicker
                selected={(task.assignees||[]).map(a=>a.id)}
                users={users}
                onToggle={(finalIds)=>{
                  onQuickUpdate(task.id,'assignee_ids',finalIds);
                }}
                onClose={()=>ce()}
              />
            : <div onClick={()=>se('assignees')} style={{display:'flex',alignItems:'center',gap:'2px',cursor:'pointer',minHeight:'22px'}}>
                {assignees.length>0
                  ? assignees.slice(0,3).map((a,ai)=>(
                      <div key={a.id} style={{marginLeft:ai>0?-6:0}} title={a.full_name}><Avatar name={a.full_name} color={a.avatar_color} size={22}/></div>
                    ))
                  : <span style={{fontSize:'11px',color:'var(--muted)'}}>+ Assign</span>}
                {assignees.length>3&&<span style={{fontSize:'10px',color:'var(--muted)',marginLeft:'4px'}}>+{assignees.length-3}</span>}
              </div>
          }
        </div>

        {/* Project Manager — inline multi-select */}
        <div style={{padding:'5px 6px',borderLeft:'1px solid var(--border)',position:'relative'}}>
          {ec('project_managers')
            ? <AssigneePicker
                selected={Array.isArray(task.project_managers)?task.project_managers:[]}
                users={users}
                onToggle={(finalIds)=>{
                  onQuickUpdate(task.id,'project_managers',finalIds);
                }}
                onClose={()=>ce()}
              />
            : <div onClick={()=>se('project_managers')} style={{display:'flex',alignItems:'center',gap:'2px',cursor:'pointer',minHeight:'22px'}}>
                {pms.length>0
                  ? pms.slice(0,2).map((u,i)=>(
                      <div key={u.id} style={{marginLeft:i>0?-6:0}} title={u.full_name}><Avatar name={u.full_name} color={u.avatar_color} size={22}/></div>
                    ))
                  : <span style={{fontSize:'11px',color:'var(--muted)'}}>+ PM</span>}
                {pms.length>2&&<span style={{fontSize:'10px',color:'var(--muted)',marginLeft:'4px'}}>+{pms.length-2}</span>}
              </div>
          }
        </div>

        {/* Weight — editable inline (shows distribution dialog if total > 100%) */}
        <div style={{padding:'5px 8px',borderLeft:'1px solid var(--border)',textAlign:'center'}}>
          {ec('task_weight')
            ? <input autoFocus type="number" min="0" max="100" className="input" defaultValue={task.task_weight??0}
                style={{width:'52px',height:'28px',fontSize:'11px',padding:'0 4px',textAlign:'center'}}
                onBlur={e=>{onWeightEdit(task,e.target.value);ce();}}
                onKeyDown={e=>{if(e.key==='Enter'){onWeightEdit(task,e.target.value);ce();}if(e.key==='Escape')ce();}}/>
            : <span onClick={()=>{ if(task.status!=='cancelled') se('task_weight'); }}
                title={task.status==='cancelled'?'Cancelled tasks have fixed weight (0)':'Click to edit weight'}
                style={{fontSize:'12px',fontWeight:'700',color:'var(--muted2)',cursor:task.status==='cancelled'?'default':'pointer',padding:'2px 6px',borderRadius:'4px'}}>
                {task.task_weight??0}%
              </span>
          }
        </div>

        {/* Progress — circle ring, click to edit */}
        <div style={{padding:'5px 8px',borderLeft:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'center'}}>
          {ec('progress')
            ? <input autoFocus type="number" min="0" max="100" className="input" defaultValue={Math.round(progress)}
                style={{width:'50px',height:'28px',fontSize:'11px',padding:'0 4px',textAlign:'center'}}
                onBlur={e=>{onQuickUpdate(task.id,'progress',e.target.value);ce();}}
                onKeyDown={e=>{if(e.key==='Enter'){onQuickUpdate(task.id,'progress',e.target.value);ce();}if(e.key==='Escape')ce();}}/>
            : <div onClick={()=>se('progress')}
                title={subs.length?'Auto-calculated from subtasks — click to override':'Click to edit progress'}
                style={{cursor:'pointer',position:'relative'}}>
                <ProgressRing value={progress} size={32} isCancelled={task.status==='cancelled'}/>
                {subs.length>0&&<div style={{position:'absolute',top:-4,right:-4,width:8,height:8,borderRadius:'50%',background:'#D97706',border:'2px solid var(--surface)'}} title="Has subtasks — manual override active"/>}
              </div>
          }
        </div>

        {/* Start date */}
        <div style={{padding:'5px 8px',borderLeft:'1px solid var(--border)',fontSize:'11px',color:'var(--muted2)',position:'relative'}}>
          {ec('start_date')
            ? <CalendarPicker value={task.start_date||''}
                onPick={(iso)=>{ onQuickUpdate(task.id,'start_date',iso); ce(); }}
                onClose={ce}/>
            : <div onClick={()=>se('start_date')} style={{cursor:'pointer'}}>{task.start_date||<span style={{color:'var(--muted)'}}>—</span>}</div>
          }
        </div>

        {/* Due date */}
        <div style={{padding:'5px 8px',borderLeft:'1px solid var(--border)',fontSize:'11px',position:'relative'}}>
          {ec('due_date')
            ? <CalendarPicker value={task.due_date||''}
                onPick={(iso)=>{ onQuickUpdate(task.id,'due_date',iso); ce(); }}
                onClose={ce}/>
            : <div onClick={()=>se('due_date')} style={{cursor:'pointer',color:isOverdue?'#DC2626':'var(--muted2)',fontWeight:isOverdue?'700':'400'}}>
                {task.due_date
                  ? <span title={isOverdue?'Overdue':''}>{task.due_date}{isOverdue&&' ⚠'}</span>
                  : <span style={{color:'var(--muted)'}}>—</span>}
              </div>
          }
        </div>

        {/* 💬 Comments — after Due Date */}
        <div style={{padding:'4px 4px',borderLeft:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'center'}}>
          <button onClick={()=>onComment(task)} title={`Comments (${task.comment_count||0})`}
            style={{background:'none',border:'none',cursor:'pointer',color:(task.comment_count||0)>0?'var(--brand)':'var(--muted)',fontSize:'13px',lineHeight:1,padding:'3px',borderRadius:'4px',transition:'color .1s',display:'flex',alignItems:'center',gap:'2px'}}
            onMouseEnter={e=>e.currentTarget.style.color='var(--brand)'}
            onMouseLeave={e=>e.currentTarget.style.color=(task.comment_count||0)>0?'var(--brand)':'var(--muted)'}>
            💬
            {(task.comment_count||0)>0&&(
              <span style={{fontSize:'10px',fontWeight:'800',color:'var(--brand)',lineHeight:1}}>
                {task.comment_count}
              </span>
            )}
          </button>
        </div>

        {/* Budget H */}
        <div style={{padding:'5px 8px',borderLeft:'1px solid var(--border)',textAlign:'center'}}>
          <div style={{fontSize:'12px',color:'#16A34A',fontWeight:'700',padding:'4px 6px'}}>{task.time_budget ? `${task.time_budget}h` : '—'}</div>
        </div>

        {/* Logged H */}
        <div style={{padding:'5px 8px',borderLeft:'1px solid var(--border)',textAlign:'center'}}>
          <div style={{fontSize:'12px',color:'#5B5BD6',fontWeight:'700',padding:'4px 6px'}}>{task.time_estimate ? `${task.time_estimate}h` : '—'}</div>
        </div>

        {/* Log Time moved to task edit modal */}

        {/* Edit btn removed from row — click task name to edit */}

        {/* Delete removed from row — available in Edit Task modal */}
      </div>

      {/* ── Subtasks when expanded */}
      {isOpen&&subs.map(st=>(
        <TaskRow key={st.id} task={st} users={users} level={level+1}
          GRID={GRID} expanded={expanded} setExpanded={setExpanded}
          editingCell={editingCell} setEditingCell={setEditingCell}
          onEdit={onEdit} onNew={onNew} onDelete={onDelete}
          onQuickUpdate={onQuickUpdate} onQuickUpdateRaw={onQuickUpdateRaw}
          onWeightModal={onWeightModal} onWeightEdit={onWeightEdit} onComment={onComment}
          onReorder={onReorder} dragTask={dragTask} dragOverTask={dragOverTask}
          dragSubtask={dragSubtask} dragOverSubtask={dragOverSubtask}
          isGuest={isGuest}/>
      ))}

      {/* ── Add child row (only down to sub-subtask: not shown at level 2) */}
      {isOpen&&!isGuest&&level<2&&(
        <div style={{
          borderBottom:'1px solid var(--border)',
          background: level===0?'rgba(91,91,214,0.018)':level===1?'rgba(110,86,207,0.035)':'rgba(139,92,246,0.05)',
          paddingLeft: (20+(level+1)*20)+'px',
          paddingTop:'7px', paddingBottom:'7px', paddingRight:'16px',
          display:'flex', alignItems:'center',
        }}>
          {/* left accent bar */}
          <div style={{
            width:'3px', height:'28px', borderRadius:'99px', flexShrink:0, marginRight:'10px',
            background: level===0?'#5B5BD6':level===1?'#6E56CF':'#8B5CF6', opacity:0.5,
          }}/>
          <button onClick={()=>onNew(task.id)}
            style={{
              display:'inline-flex', alignItems:'center', gap:'6px',
              cursor:'pointer', borderRadius:'6px',
              fontSize:'11px', fontWeight:'600',
              padding:'5px 12px',
              border: `1.5px solid ${level===0?'#5B5BD6':level===1?'#6E56CF':'#8B5CF6'}`,
              background:'transparent',
              color: level===0?'#5B5BD6':level===1?'#6E56CF':'#8B5CF6',
              transition:'all .15s',
            }}
            onMouseEnter={e=>{
              e.currentTarget.style.background=level===0?'#5B5BD6':level===1?'#6E56CF':'#8B5CF6';
              e.currentTarget.style.color='#fff';
            }}
            onMouseLeave={e=>{
              e.currentTarget.style.background='transparent';
              e.currentTarget.style.color=level===0?'#5B5BD6':level===1?'#6E56CF':'#8B5CF6';
            }}>
            <span style={{fontSize:'15px',fontWeight:'700',lineHeight:1}}>+</span>
            <span>{level===0?'Subtask':'Sub-subtask'}</span>
          </button>
        </div>
      )}
    </div>
  );
}


function SearchableUserSelect({ users, selected, onToggle, accentColor, accentBg, placeholder }) {
  const [search, setSearch] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = users.filter(u => u.full_name.toLowerCase().includes(search.toLowerCase()));
  const selectedUsers = users.filter(u => selected.includes(u.id));

  return (
    <div ref={ref} style={{position:'relative'}}>
      {/* Selected chips */}
      {selectedUsers.length > 0 && (
        <div style={{display:'flex',flexWrap:'wrap',gap:'4px',marginBottom:'6px'}}>
          {selectedUsers.map(u => (
            <span key={u.id} style={{display:'flex',alignItems:'center',gap:'4px',padding:'3px 8px',borderRadius:'99px',background:accentBg,border:`1.5px solid ${accentColor}`,fontSize:'12px',color:accentColor,fontWeight:'600'}}>
              <Avatar name={u.full_name} color={u.avatar_color} size={16}/>
              {u.full_name}
              <button type="button" onClick={()=>onToggle(u.id)}
                style={{background:'none',border:'none',cursor:'pointer',color:accentColor,fontSize:'14px',lineHeight:1,padding:'0 0 0 2px',fontWeight:'700'}}>×</button>
            </span>
          ))}
        </div>
      )}
      {/* Search input */}
      <div style={{position:'relative'}}>
        <input
          className="input"
          type="text"
          placeholder={placeholder||'Search...'}
          value={search}
          onChange={e=>{ setSearch(e.target.value); setOpen(true); }}
          onFocus={()=>setOpen(true)}
          style={{width:'100%',paddingRight:'32px'}}
        />
        <span style={{position:'absolute',right:'10px',top:'50%',transform:'translateY(-50%)',color:'var(--muted)',fontSize:'12px',pointerEvents:'none'}}>
          {open ? '▲' : '▼'}
        </span>
      </div>
      {/* Dropdown */}
      {open && (
        <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,zIndex:200,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'12px',boxShadow:'0 12px 40px rgba(0,0,0,0.15)',maxHeight:'280px',overflowY:'auto',minWidth:'320px'}}>
          {filtered.length === 0
            ? <div style={{padding:'12px',fontSize:'12px',color:'var(--muted)',textAlign:'center'}}>No users found</div>
            : filtered.map(u => {
                const sel = selected.includes(u.id);
                return (
                  <div key={u.id} onClick={()=>{ onToggle(u.id); setSearch(''); }}
                    style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 14px',cursor:'pointer',background:sel?accentBg:'transparent',borderBottom:'1px solid var(--border)',transition:'background .1s'}}
                    onMouseEnter={e=>{ if(!sel) e.currentTarget.style.background='var(--surface2)'; }}
                    onMouseLeave={e=>{ if(!sel) e.currentTarget.style.background='transparent'; }}>
                    <Avatar name={u.full_name} color={u.avatar_color} size={24}/>
                    <span style={{fontSize:'14px',color:'var(--text)',fontWeight:sel?'700':'400',flex:1}}>{u.full_name}</span>
                    {sel && <span style={{color:accentColor,fontSize:'16px',fontWeight:'700'}}>✓</span>}
                  </div>
                );
              })
          }
        </div>
      )}
    </div>
  );
}

// ── CalendarPicker — professional popover calendar (day/month/year) ─────────
function CalendarPicker({ value, onPick, onClose, minDate }) {
  const parseIso = (v) => {
    if (v && /^\d{4}-\d{2}-\d{2}$/.test(v)) { const [y,m,d]=v.split('-').map(Number); return new Date(y, m-1, d); }
    return null;
  };
  const minD = parseIso(minDate);
  const selected = parseIso(value);
  const today = new Date();
  const [view, setView] = React.useState(() => selected || today); // month being shown
  const [mode, setMode] = React.useState('days'); // 'days' | 'months' | 'years'
  const ref = React.useRef(null);

  React.useEffect(() => {
    function onDoc(e){ if(ref.current && !ref.current.contains(e.target)) onClose(); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const DOW = ['Mo','Tu','We','Th','Fr','Sa','Su'];
  const y = view.getFullYear(), m = view.getMonth();
  const firstDay = new Date(y, m, 1);
  // Monday-first offset
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(y, m+1, 0).getDate();
  const isSel = (d) => selected && selected.getFullYear()===y && selected.getMonth()===m && selected.getDate()===d;
  const isToday = (d) => today.getFullYear()===y && today.getMonth()===m && today.getDate()===d;

  function pick(d) {
    const iso = `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    onPick(iso);
  }

  const cells = [];
  for (let i=0;i<startOffset;i++) cells.push(null);
  for (let d=1;d<=daysInMonth;d++) cells.push(d);

  const btn = { background:'none',border:'none',cursor:'pointer',color:'var(--text)',borderRadius:'6px' };
  const yearStart = Math.floor(y/12)*12;

  return (
    <div ref={ref} style={{position:'absolute',top:'calc(100% + 4px)',left:0,zIndex:1000,
      background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'12px',
      boxShadow:'0 12px 32px rgba(0,0,0,0.22)',padding:'12px',width:'248px',userSelect:'none'}}
      onMouseDown={e=>e.stopPropagation()}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'10px'}}>
        <button style={{...btn,padding:'4px 8px',fontSize:'16px',color:'var(--muted)'}}
          onClick={()=>{ if(mode==='days') setView(new Date(y,m-1,1)); else if(mode==='months') setView(new Date(y-1,m,1)); else setView(new Date(y-12,m,1)); }}>‹</button>
        <div style={{display:'flex',gap:'6px'}}>
          <button style={{...btn,fontWeight:'700',fontSize:'13px',padding:'4px 8px'}}
            onClick={()=>setMode(mode==='months'?'days':'months')}>{MONTHS[m]}</button>
          <button style={{...btn,fontWeight:'700',fontSize:'13px',padding:'4px 8px'}}
            onClick={()=>setMode(mode==='years'?'days':'years')}>{y}</button>
        </div>
        <button style={{...btn,padding:'4px 8px',fontSize:'16px',color:'var(--muted)'}}
          onClick={()=>{ if(mode==='days') setView(new Date(y,m+1,1)); else if(mode==='months') setView(new Date(y+1,m,1)); else setView(new Date(y+12,m,1)); }}>›</button>
      </div>

      {mode==='days' && (<>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:'2px',marginBottom:'4px'}}>
          {DOW.map(d=><div key={d} style={{textAlign:'center',fontSize:'10px',fontWeight:'700',color:'var(--muted)',padding:'4px 0'}}>{d}</div>)}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:'2px'}}>
          {cells.map((d,i)=> d===null
            ? <div key={i}/>
            : (() => {
                const cellDate = new Date(y, m, d);
                const disabled = minD && cellDate < minD;
                return <button key={i} disabled={disabled} onClick={()=>{ if(!disabled) pick(d); }}
                  title={disabled ? 'Before the start date' : ''}
                  style={{...btn,height:'30px',fontSize:'12px',
                    fontWeight: isSel(d)||isToday(d)?'700':'500',
                    cursor: disabled?'not-allowed':'pointer',
                    opacity: disabled?0.32:1,
                    color: disabled?'var(--muted)':isSel(d)?'#fff':isToday(d)?'var(--brand)':'var(--text)',
                    background: isSel(d)?'var(--brand)':'transparent',
                    border: isToday(d)&&!isSel(d)?'1px solid var(--brand)':'1px solid transparent',
                    textDecoration: disabled?'line-through':'none'}}
                  onMouseEnter={e=>{ if(!isSel(d)&&!disabled) e.currentTarget.style.background='var(--surface2)'; }}
                  onMouseLeave={e=>{ if(!isSel(d)&&!disabled) e.currentTarget.style.background='transparent'; }}>
                  {d}
                </button>;
              })()
          )}
        </div>
      </>)}

      {mode==='months' && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'6px'}}>
          {MONTHS_SHORT.map((mn,i)=>(
            <button key={mn} onClick={()=>{ setView(new Date(y,i,1)); setMode('days'); }}
              style={{...btn,padding:'10px 0',fontSize:'12px',fontWeight:i===m?'700':'500',
                color:i===m?'#fff':'var(--text)',background:i===m?'var(--brand)':'var(--surface2)'}}>{mn}</button>
          ))}
        </div>
      )}

      {mode==='years' && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'6px'}}>
          {Array.from({length:12},(_, i)=>yearStart+i).map(yr=>(
            <button key={yr} onClick={()=>{ setView(new Date(yr,m,1)); setMode('months'); }}
              style={{...btn,padding:'10px 0',fontSize:'12px',fontWeight:yr===y?'700':'500',
                color:yr===y?'#fff':'var(--text)',background:yr===y?'var(--brand)':'var(--surface2)'}}>{yr}</button>
          ))}
        </div>
      )}

      {/* Footer */}
      <div style={{display:'flex',justifyContent:'space-between',marginTop:'10px',paddingTop:'8px',borderTop:'1px solid var(--border)'}}>
        <button style={{...btn,fontSize:'11px',color:'var(--muted)',padding:'4px 8px'}} onClick={()=>onPick('')}>Clear</button>
        <button style={{...btn,fontSize:'11px',fontWeight:'700',color:'var(--brand)',padding:'4px 8px'}}
          onClick={()=>{ const t=new Date(); onPick(`${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`); }}>Today</button>
      </div>
    </div>
  );
}

// ── CalendarField — a click-to-open calendar field for forms (with min date) ──
function CalendarField({ value, onPick, minDate, placeholder, disabled }) {
  const [open, setOpen] = React.useState(false);
  const display = (value && /^\d{4}-\d{2}-\d{2}$/.test(value))
    ? (() => { const [y,m,d]=value.split('-'); return `${d}/${m}/${y}`; })()
    : '';
  return (
    <div style={{position:'relative'}}>
      <button type="button" disabled={disabled} onClick={()=>setOpen(o=>!o)} className="input"
        style={{width:'100%',textAlign:'left',cursor:disabled?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'space-between',background:'var(--surface)',opacity:disabled?0.6:1}}>
        <span style={{color: display ? 'var(--text)' : 'var(--muted)'}}>{display || (placeholder || 'Select date')}</span>
        <span style={{fontSize:'13px'}}>📅</span>
      </button>
      {open && <CalendarPicker value={value} minDate={minDate}
        onPick={(iso)=>{ onPick(iso); setOpen(false); }} onClose={()=>setOpen(false)}/>}
    </div>
  );
}

// ── DateMaskInput — DD/MM/YYYY mask, only fires onChange when fully valid ────
function DateMaskInput({ value, onChange, style, className, ...props }) {
  const [display, setDisplay] = React.useState('');
  
  // Convert ISO (YYYY-MM-DD) → DD/MM/YYYY for display
  React.useEffect(() => {
    if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y,m,d] = value.split('-');
      setDisplay(`${d}/${m}/${y}`);
    } else if (!value) {
      setDisplay('');
    }
  }, [value]);

  function handleChange(e) {
    let v = e.target.value.replace(/[^0-9/]/g, '');
    // Auto-insert slashes
    if (v.length === 2 && display.length === 1) v = v + '/';
    if (v.length === 5 && display.length === 4) v = v + '/';
    if (v.length > 10) v = v.slice(0, 10);
    setDisplay(v);
    // Only fire onChange when we have a complete valid date
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
      const [dd, mm, yyyy] = v.split('/');
      const d = parseInt(dd), m = parseInt(mm), y = parseInt(yyyy);
      if (y >= 1900 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        const iso = `${yyyy}-${mm.padStart(2,'0')}-${dd.padStart(2,'0')}`;
        onChange({ target: { value: iso } });
      }
    } else if (v === '') {
      onChange({ target: { value: '' } });
    }
  }

  return (
    <input
      type="text"
      className={className}
      value={display}
      onChange={handleChange}
      onBlur={props.onBlur}
      onKeyDown={props.onKeyDown}
      autoFocus={props.autoFocus}
      placeholder="DD/MM/YYYY"
      maxLength={10}
      style={{ ...style, fontVariantNumeric: 'tabular-nums' }}
    />
  );
}


function TaskModal({task,parentId,users,listId,projectId,onDelete,onSave,onClose,tasks,onWeightError,depth=0}) {
  const isSubtask=!!parentId||!!task?.parent_task_id;
  const taskLabel = depth===0?'Task':depth===1?'Subtask':'Sub-subtask';
  const [form,setForm]=useState({
    name:task?.name||'', task_type:task?.task_type||'task',
    status:task?.status||'not_started', priority:task?.priority||'normal',
    assignee_ids:task?.assignees?.map(a=>a.id)||[],
    project_managers:Array.isArray(task?.project_managers)?task.project_managers:[],
    start_date:task?.start_date||'', due_date:task?.due_date||'',
    actual_start_date:task?.actual_start_date||(task?.start_date||''), actual_end_date:task?.actual_end_date||(task?.due_date||''),
    task_weight:task?.task_weight??0,
    progress:Math.min(100,task?.progress||0),
    time_estimate:task?.time_estimate||'', time_budget:task?.time_budget||'',
    is_private:task?.is_private||false,
    is_major_milestone:task?.is_major_milestone||false,
    description:task?.description||'',
  });
  const [activeTab, setActiveTab] = useState('details');
  const [showLogTimeInModal, setShowLogTimeInModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deps, setDeps] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attUploading, setAttUploading] = useState(false);
  const [attError, setAttError] = useState('');
  const [attDeleteConfirm, setAttDeleteConfirm] = useState(null); // {id, name}
  const [allTasks, setAllTasks] = useState([]);
  const [depSearch, setDepSearch] = useState('');
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));
  const tog=(k,id)=>f(k,form[k].includes(id)?form[k].filter(x=>x!==id):[...form[k],id]);

  useEffect(()=>{
    if (task?.id) {
      api.get(`/tasks/${task.id}/dependencies`).then(d=>setDeps(Array.isArray(d)?d:[])).catch(()=>{});
      api.get(`/tasks/${task.id}/attachments`).then(a=>setAttachments(Array.isArray(a)?a:[])).catch(()=>{});
    }
    // Load all visible tasks for dep picker
    if (listId) {
      api.get(`/lists/${listId}`).then(info=>{
        if (info?.project_id) {
          api.get(`/projects/${info.project_id}/tasks-for-deps`).then(t=>setAllTasks(Array.isArray(t)?t:[])).catch(()=>{});
        }
      }).catch(()=>{});
    }
  },[task?.id]);

  async function addDep(depTaskId) {
    if (!task?.id) return;
    await api.post(`/tasks/${task.id}/dependencies`,{depends_on_task_id:depTaskId}).catch(()=>{});
    const d = await api.get(`/tasks/${task.id}/dependencies`).catch(()=>[]);
    setDeps(Array.isArray(d)?d:[]);
  }
  async function removeDep(depId) {
    if (!task?.id) return;
    await api.delete(`/tasks/${task.id}/dependencies/${depId}`).catch(()=>{});
    setDeps(prev=>prev.filter(d=>d.id!==depId));
  }

  const filteredTasks = allTasks.filter(t=>
    t.id !== task?.id &&
    !deps.find(d=>d.id===t.id) &&
    t.name.toLowerCase().includes(depSearch.toLowerCase())
  );

  const STATUS_COLORS_MODAL = {not_started:'#8A93A6',planning:'#2563EB',in_progress:'#5B5BD6',escalated:'#DC2626',at_risk:'#D97706',on_hold:'#8A93A6',completed:'#16A34A',cancelled:'#6B7280'};

  return (
    <div className="modal-overlay">
      <div className="modal" style={{width:'820px',maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0',paddingBottom:'14px',borderBottom:'1px solid var(--border)'}}>
          <div>
            <h3 style={{fontSize:'16px',fontWeight:'700',margin:'0 0 2px'}}>{task?.id?'Edit ':'New '}{taskLabel}</h3>
            {task?.name&&<div style={{fontSize:'12px',color:'var(--muted)',maxWidth:'500px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{task.name}</div>}
          </div>
          <div style={{display:'flex',gap:'8px',alignItems:'center'}}>

            {task?.id&&onDelete&&(
              <button type="button"
                onClick={()=>setConfirmDelete(true)}
                style={{display:'flex',alignItems:'center',gap:'6px',padding:'6px 14px',borderRadius:'8px',border:'1.5px solid #DC2626',background:'rgba(220,38,38,0.08)',color:'#DC2626',cursor:'pointer',fontSize:'12px',fontWeight:'700',transition:'all .15s'}}
                onMouseEnter={e=>{e.currentTarget.style.background='#DC2626';e.currentTarget.style.color='#fff';}}
                onMouseLeave={e=>{e.currentTarget.style.background='rgba(220,38,38,0.08)';e.currentTarget.style.color='#DC2626';}}>
                🗑 Delete
              </button>
            )}
            <button className="btn btn-sm" onClick={onClose}>✕ Close</button>
          </div>
        </div>

        {/* Tabs — only show Dependencies for existing tasks */}
        {task?.id && (
          <div style={{display:'flex',gap:'2px',marginBottom:'16px',marginTop:'12px',borderBottom:'1px solid var(--border)',paddingBottom:'0'}}>
            {['details','dependencies','attachments'].map(tab=>(
              <button key={tab} onClick={()=>setActiveTab(tab)}
                style={{padding:'7px 16px',background:'none',border:'none',borderBottom:activeTab===tab?'2px solid var(--brand)':'2px solid transparent',cursor:'pointer',fontSize:'12px',fontWeight:activeTab===tab?'700':'500',color:activeTab===tab?'var(--brand)':'var(--muted)',marginBottom:'-1px',textTransform:'capitalize',transition:'all .15s'}}>
                {tab==='dependencies'?`🔗 Dependencies (${deps.length})`:tab==='attachments'?`📎 Attachments (${attachments.length})`:tab.charAt(0).toUpperCase()+tab.slice(1)}
              </button>
            ))}
          </div>
        )}
        {task?.id && activeTab==='dependencies' ? (
          /* ── Dependencies tab */
          <div>
            <div style={{marginBottom:'16px'}}>
              <label className="form-label">This task depends on (must be completed first):</label>
              {deps.length===0
                ? <div style={{fontSize:'12px',color:'var(--muted)',padding:'12px',border:'1px dashed var(--border)',borderRadius:'var(--radius-lg)',textAlign:'center'}}>No dependencies yet</div>
                : <div style={{display:'flex',flexDirection:'column',gap:'6px',marginBottom:'10px'}}>
                    {deps.map(d=>(
                      <div key={d.id} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 12px',background:'var(--surface2)',borderRadius:'var(--radius)',border:'1px solid var(--border)'}}>
                        <div style={{width:8,height:8,borderRadius:'50%',background:STATUS_COLORS_MODAL[d.status]||'#8A93A6',flexShrink:0}}/>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:'13px',fontWeight:'600',color:'var(--text)'}}>{d.name}</div>
                          <div style={{fontSize:'10px',color:'var(--muted)'}}>{d.phase_name?d.phase_name+' · ':''}{d.list_name} · {d.status?.replace(/_/g,' ')} · {Math.round(d.progress||0)}%</div>
                        </div>
                        <button onClick={()=>removeDep(d.id)}
                          style={{background:'rgba(220,38,38,0.08)',border:'1px solid rgba(220,38,38,0.2)',borderRadius:'6px',padding:'3px 8px',cursor:'pointer',color:'#DC2626',fontSize:'11px',fontWeight:'600',flexShrink:0}}>
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
              }
            </div>
            <div>
              <label className="form-label">Add dependency (tasks visible to you):</label>
              <input className="input" placeholder="Search tasks…" value={depSearch} onChange={e=>setDepSearch(e.target.value)} style={{marginBottom:'8px'}}/>
              <div style={{maxHeight:'240px',overflowY:'auto',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',display:'flex',flexDirection:'column',gap:0}}>
                {filteredTasks.length===0
                  ? <div style={{fontSize:'12px',color:'var(--muted)',padding:'16px',textAlign:'center'}}>No tasks found</div>
                  : (() => {
                      // Group by Phase → List for easy browsing
                      const byPhase = {};
                      filteredTasks.forEach(t => {
                        const ph = t.phase_name || 'No Phase';
                        const li = t.list_name || 'No List';
                        byPhase[ph] = byPhase[ph] || {};
                        byPhase[ph][li] = byPhase[ph][li] || [];
                        byPhase[ph][li].push(t);
                      });
                      return Object.keys(byPhase).sort().map(ph => (
                        <div key={ph}>
                          <div style={{position:'sticky',top:0,background:'var(--surface2)',padding:'5px 12px',fontSize:'10px',fontWeight:'800',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--brand)',borderBottom:'1px solid var(--border)'}}>
                            📁 {ph}
                          </div>
                          {Object.keys(byPhase[ph]).sort().map(li => (
                            <div key={li}>
                              <div style={{padding:'4px 12px 4px 22px',fontSize:'10px',fontWeight:'700',color:'var(--muted2)',background:'var(--surface)'}}>
                                {li}
                              </div>
                              {byPhase[ph][li].map(t => (
                                <div key={t.id} onClick={()=>addDep(t.id)}
                                  style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 12px 8px 28px',borderBottom:'1px solid var(--border)',cursor:'pointer',transition:'background .1s'}}
                                  onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
                                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                  <div style={{width:8,height:8,borderRadius:'50%',background:STATUS_COLORS_MODAL[t.status]||'#8A93A6',flexShrink:0}}/>
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:'12px',fontWeight:'600',color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.name}</div>
                                    <div style={{fontSize:'10px',color:'var(--muted)'}}>{Math.round(t.progress||0)}%{t.due_date?' · due '+t.due_date:''}</div>
                                  </div>
                                  <span style={{fontSize:'11px',color:'var(--brand)',fontWeight:'700',flexShrink:0}}>+ Add</span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      ));
                    })()
                }
              </div>
            </div>
            <div style={{display:'flex',justifyContent:'flex-end',marginTop:'20px',paddingTop:'14px',borderTop:'1px solid var(--border)'}}>
              <button className="btn" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : task?.id && activeTab==='attachments' ? (
          /* ── Attachments tab ── */
          <div>
            <div style={{marginBottom:'14px',display:'flex',alignItems:'center',gap:'10px'}}>
              <label style={{display:'inline-flex',alignItems:'center',gap:'8px',padding:'8px 16px',background:'var(--brand)',color:'white',borderRadius:'var(--radius)',cursor:attUploading?'not-allowed':'pointer',fontSize:'12px',fontWeight:'700',opacity:attUploading?0.7:1,flexShrink:0}}>
                {attUploading?<><span style={{display:'inline-block',width:12,height:12,border:'2px solid white',borderTopColor:'transparent',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>Uploading…</>:'📎 Attach File'}
                <input type="file" style={{display:'none'}} disabled={attUploading}
                  onChange={async e=>{
                    const file=e.target.files?.[0]; e.target.value='';
                    if(!file) return;
                    if(file.size>25*1024*1024){setAttError('File too large — max 25 MB');return;}
                    setAttUploading(true); setAttError('');
                    try {
                      const fd=new FormData(); fd.append('file',file);
                      const token=localStorage.getItem('flowup_token');
                      const res=await fetch(`/api/tasks/${task.id}/attachments`,{method:'POST',headers:{Authorization:`Bearer ${token}`},body:fd});
                      if(!res.ok){const err=await res.json();throw new Error(err.error||'Upload failed');}
                      const fresh=await fetch(`/api/tasks/${task.id}/attachments`,{headers:{Authorization:`Bearer ${token}`}});
                      const list=await fresh.json();
                      setAttachments(Array.isArray(list)?list:[]);
                    } catch(err){setAttError(err.message||'Upload failed');}
                    setAttUploading(false);
                  }}/>
              </label>
              <span style={{fontSize:'11px',color:'var(--muted)'}}>Max 25 MB · Executables not allowed</span>
            </div>
            {attError&&<div style={{fontSize:'12px',color:'#DC2626',marginBottom:'10px',padding:'8px 12px',background:'#FEF2F2',borderRadius:'var(--radius)',border:'1px solid #FECACA'}}>{attError}</div>}
            {attachments.length===0
              ? <div style={{textAlign:'center',padding:'28px',border:'1px dashed var(--border)',borderRadius:'var(--radius-lg)',color:'var(--muted)',fontSize:'12px'}}>
                  <div style={{fontSize:'28px',marginBottom:'8px'}}>📎</div>
                  No attachments yet
                </div>
              : <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                  {attachments.map(att=>{
                    const sizeStr=att.file_size>1024*1024?(att.file_size/1024/1024).toFixed(1)+' MB':att.file_size>1024?Math.round(att.file_size/1024)+' KB':att.file_size+' B';
                    const ext=att.file_name.split('.').pop()?.toUpperCase()||'FILE';
                    const extColors={PDF:'#DC2626',DOC:'#2563EB',DOCX:'#2563EB',XLS:'#16A34A',XLSX:'#16A34A',PNG:'#7C3AED',JPG:'#7C3AED',JPEG:'#7C3AED',ZIP:'#D97706',RAR:'#D97706'};
                    const extColor=extColors[ext]||'#64748B';
                    const token=localStorage.getItem('flowup_token');
                    return (
                      <div key={att.id} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 14px',background:'var(--surface2)',borderRadius:'var(--radius)',border:'1px solid var(--border)'}}>
                        <div style={{width:36,height:36,borderRadius:'6px',background:extColor+'18',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                          <span style={{fontSize:'9px',fontWeight:'800',color:extColor}}>{ext.slice(0,4)}</span>
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:'12px',fontWeight:'600',color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={att.file_name}>{att.file_name}</div>
                          <div style={{fontSize:'10px',color:'var(--muted)',marginTop:'2px'}}>{sizeStr} · {att.uploaded_by_name||'—'} · {new Date(att.created_at).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}</div>
                        </div>
                        <button onClick={()=>{
                          fetch(`/api/attachments/${att.id}/download`,{headers:{Authorization:`Bearer ${token}`}})
                            .then(r=>r.blob()).then(blob=>{const url=URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download=att.file_name;a.click();setTimeout(()=>URL.revokeObjectURL(url),1000);}).catch(()=>{});
                        }} style={{padding:'5px 12px',background:'var(--brand)',color:'white',borderRadius:'6px',fontSize:'11px',fontWeight:'600',border:'none',cursor:'pointer',flexShrink:0}}>⬇ Download</button>
                        <button onClick={()=>setAttDeleteConfirm({id:att.id,name:att.file_name})}
                          style={{padding:'5px 8px',background:'none',border:'1px solid #FCA5A5',borderRadius:'6px',cursor:'pointer',color:'#DC2626',fontSize:'11px',fontWeight:'600',flexShrink:0}}>🗑</button>
                      </div>
                    );
                  })}
                </div>
            }
            <div style={{display:'flex',justifyContent:'flex-end',marginTop:'20px',paddingTop:'14px',borderTop:'1px solid var(--border)'}}>
              <button className="btn" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
          {/* Name */}
          <div className="form-group" style={{gridColumn:'1/3'}}>
            <label className="form-label">Task Name *</label>
            <input className="input" value={form.name} onChange={e=>f('name',e.target.value)} placeholder="Enter task name…" autoFocus/>
          </div>

          {/* Type */}
          <div className="form-group">
            <label className="form-label">Type</label>
            <select className="select" value={form.task_type} onChange={e=>f('task_type',e.target.value)}>
              {TYPE_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Status */}
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="select" value={form.status} onChange={e=>f('status',e.target.value)}>
              {STATUS_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Priority */}
          <div className="form-group">
            <label className="form-label">Priority</label>
            <select className="select" value={form.priority} onChange={e=>f('priority',e.target.value)}>
              {PRIORITY_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Time estimate */}
          <div className="form-group">
            <label className="form-label">Time Budget (hours) <span style={{fontSize:'10px',fontWeight:'400',color:'var(--muted)'}}>planned estimate</span></label>
            <input className="input" type="number" min="0" step="0.5"
              value={form.time_budget}
              onChange={e=>f('time_budget',e.target.value)}
              placeholder="e.g. 10"/>
            {/* Diff hint — only shows when user actually changes the value */}
            {(()=>{
              const original = task?.time_budget || 0;
              const current = parseFloat(form.time_budget)||0;
              const diff = current - original;
              if (!form.time_budget || diff === 0) return null;
              // Find parent task to show its new total (inline helper — TaskModal scope)
              function findInTree(list, id) {
                for (const t of (list||[])) {
                  if (t.id===id) return t;
                  const f = findInTree(t.subtasks||[], id);
                  if (f) return f;
                }
                return null;
              }
              const parentTask = task?.parent_task_id
                ? findInTree(tasks, task.parent_task_id)
                : null;
              const parentCurrentBudget = parentTask?.time_budget || 0;
              const parentNewBudget = parentCurrentBudget + diff;
              return (
                <div style={{marginTop:'6px',padding:'10px 12px',borderRadius:'8px',fontSize:'11px',
                  background: diff>0?'#EFF6FF':'#FFF7ED',
                  border: `1px solid ${diff>0?'#BFDBFE':'#FED7AA'}`,
                  color: diff>0?'#1E40AF':'#92400E'}}>
                  <div style={{fontWeight:'700',marginBottom:'4px'}}>
                    {diff>0?'▲ Increasing':'▼ Reducing'} by <strong>{Math.abs(diff)}h</strong>
                    {' '}· was {original}h → now {current}h
                  </div>
                  {parentTask&&(
                    <div style={{color:'#5B5BD6',marginTop:'3px'}}>
                      Parent "<strong>{parentTask.name}</strong>" budget:
                      {' '}{parentCurrentBudget}h → <strong>{Math.max(0,parentNewBudget)}h</strong>
                      {' '}after save
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="form-group">
            <label className="form-label">Logged Hours <span style={{fontSize:'10px',fontWeight:'400',color:'var(--muted)'}}>auto from Resources page</span></label>
            <div style={{display:'flex',alignItems:'center',gap:'8px',padding:'8px 12px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',fontSize:'13px'}}>
              <span style={{fontWeight:'700',color:'#5B5BD6'}}>{form.time_estimate ? `${form.time_estimate}h` : '—'}</span>
              <span style={{fontSize:'11px',color:'var(--muted)'}}>Sum of all resource log entries</span>
            </div>
          </div>

          {/* Weight */}
          <div className="form-group">
            <label className="form-label">Weight</label>
            <input className="input" type="number" min="0" value={form.task_weight} onChange={e=>f('task_weight',e.target.value)}/>
          </div>

          {/* Progress */}
          <div className="form-group">
            <label className="form-label">Progress % (0-100)</label>
            <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
              <input type="range" min="0" max="100" value={form.progress} onChange={e=>f('progress',parseInt(e.target.value))} style={{flex:1,accentColor:'var(--brand)'}}/>
              <input className="input" type="number" min="0" max="100" value={form.progress} onChange={e=>f('progress',Math.min(100,Math.max(0,parseInt(e.target.value)||0)))} style={{width:'60px',textAlign:'center',fontWeight:'700'}}/>
            </div>
            <div style={{marginTop:'6px',display:'flex',justifyContent:'center'}}><ProgressRing value={form.progress} size={40}/></div>
          </div>

          {/* Dates — 2x2 grid spanning full width */}
          <div style={{gridColumn:'1/3',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            {/* Hide start date for milestone tasks — only due date matters */}
            {form.task_type !== 'milestone' && <div className="form-group" style={{margin:0}}>
              <label className="form-label">Planned Start</label>
              <CalendarField value={form.start_date} placeholder="Select start date"
                onPick={(iso)=>{
                  f('start_date',iso); f('actual_start_date',iso);
                  // If the end date is now before the new start, clear it so the
                  // user re-picks a valid end (end can never precede start).
                  if (iso && form.due_date && form.due_date < iso) { f('due_date',''); f('actual_end_date',''); }
                }}/>
            </div>}
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Planned End{form.start_date && form.task_type!=='milestone' ? <span style={{fontWeight:'400',color:'var(--muted)',fontSize:'10px'}}> (on/after start)</span> : null}</label>
              <CalendarField value={form.due_date} placeholder="Select end date"
                minDate={form.task_type !== 'milestone' ? form.start_date : ''}
                onPick={(iso)=>{ f('due_date',iso); f('actual_end_date',iso); }}/>
            </div>
            {form.task_type !== 'milestone' && <div className="form-group" style={{margin:0}}>
              <label className="form-label">Actual Start</label>
              <CalendarField value={form.actual_start_date} placeholder="Select actual start"
                onPick={(iso)=>{ f('actual_start_date',iso); if (iso && form.actual_end_date && form.actual_end_date < iso) f('actual_end_date',''); }}/>
            </div>}
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Actual End{form.actual_start_date && form.task_type!=='milestone' ? <span style={{fontWeight:'400',color:'var(--muted)',fontSize:'10px'}}> (on/after start)</span> : null}</label>
              <CalendarField value={form.actual_end_date} placeholder="Select actual end"
                minDate={form.task_type !== 'milestone' ? form.actual_start_date : ''}
                onPick={(iso)=>f('actual_end_date',iso)}/>
            </div>
          </div>

          {/* Assignees — searchable dropdown */}
          <div className="form-group" style={{gridColumn:'1/3'}}>
            <label className="form-label">Assignees</label>
            <SearchableUserSelect
              users={users} selected={form.assignee_ids}
              onToggle={id=>tog('assignee_ids',id)}
              accentColor="var(--brand)" accentBg="var(--brand-dim)"
              placeholder="Search assignees..."/>
          </div>

          {/* Project Managers — searchable dropdown */}
          <div className="form-group" style={{gridColumn:'1/3'}}>
            <label className="form-label">Project Managers</label>
            <SearchableUserSelect
              users={users} selected={form.project_managers}
              onToggle={id=>tog('project_managers',id)}
              accentColor="#16A34A" accentBg="rgba(22,163,74,0.08)"
              placeholder="Search project managers..."/>
          </div>

          {/* Description */}
          <div className="form-group" style={{gridColumn:'1/3'}}>
            <label className="form-label">Description</label>
            <textarea className="input" rows={3} value={form.description} onChange={e=>f('description',e.target.value)} placeholder="Add context or notes…" style={{resize:'vertical'}}/>
          </div>

          {/* Private */}
          <label style={{gridColumn:'1/3',display:'flex',alignItems:'center',gap:'8px',fontSize:'13px',cursor:'pointer',padding:'4px 0'}}>
            <input type="checkbox" checked={form.is_private} onChange={e=>f('is_private',e.target.checked)}/>
            Restricted Access — visible only to assignees & project managers
          </label>

          {/* Major milestone */}
          <label style={{gridColumn:'1/3',display:'flex',alignItems:'center',gap:'8px',fontSize:'13px',cursor:'pointer',padding:'4px 0'}}>
            <input type="checkbox" checked={form.is_major_milestone} onChange={e=>f('is_major_milestone',e.target.checked)}/>
            <span>⭐ Major task / milestone — flag this for executive reporting</span>
          </label>
        </div>

        <div style={{display:'flex',gap:'10px',justifyContent:'flex-end',marginTop:'20px',paddingTop:'14px',borderTop:'1px solid var(--border)'}}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={()=>{
            const {time_estimate,...saveData}=form;
            const newW=parseFloat(form.task_weight)||0;
            const isSubtask=!!(parentId||task?.parent_task_id);
            if(!isSubtask){
              const sibs=tasks.filter(t=>!t.parent_task_id&&t.id!==(task?.id));
              const othersTotal=sibs.reduce((s,t)=>s+(t.task_weight||0),0);
              if(othersTotal+newW>100 && sibs.length>0){
                // Trigger the weight redistribution modal via parent
                if(onWeightError) onWeightError(othersTotal+newW, othersTotal, 'task', sibs, newW, {...saveData,time_budget:parseFloat(form.time_budget)||0});
                return;
              }
            }
            onSave({...saveData,time_budget:parseFloat(form.time_budget)||0});
          }} disabled={!form.name.trim()}>
            {task?.id?'Save Changes':'Create Task'}
          </button>
        </div>
        </div>
        )}
      </div>

      {/* In-app delete confirmation */}
      {confirmDelete&&(
        <DeleteConfirmModal
          taskName={task?.name}
          onConfirm={()=>{setConfirmDelete(false);onDelete(task.id,task.name);onClose();}}
          onCancel={()=>setConfirmDelete(false)}
        />
      )}

      {/* In-app attachment delete confirmation */}
      {attDeleteConfirm&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',zIndex:10001,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'var(--surface)',borderRadius:'14px',padding:'28px',width:'380px',maxWidth:'95vw',boxShadow:'0 20px 60px rgba(0,0,0,.3)'}}>
            <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'14px'}}>
              <div style={{width:40,height:40,borderRadius:'10px',background:'rgba(220,38,38,0.1)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'20px',flexShrink:0}}>🗑</div>
              <div>
                <div style={{fontSize:'14px',fontWeight:'800',color:'var(--text)'}}>Delete Attachment</div>
                <div style={{fontSize:'11px',color:'var(--muted)',marginTop:'2px'}}>This cannot be undone</div>
              </div>
            </div>
            <div style={{background:'rgba(220,38,38,0.06)',border:'1px solid rgba(220,38,38,0.2)',borderRadius:'8px',padding:'10px 14px',marginBottom:'20px',fontSize:'12px',color:'var(--text)',lineHeight:'1.6',wordBreak:'break-all'}}>
              <strong>{attDeleteConfirm.name}</strong>
            </div>
            <div style={{display:'flex',gap:'10px',justifyContent:'flex-end'}}>
              <button onClick={()=>setAttDeleteConfirm(null)}
                style={{padding:'8px 18px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',color:'var(--text)'}}>
                Cancel
              </button>
              <button onClick={async()=>{
                const {id,name}=attDeleteConfirm;
                setAttDeleteConfirm(null);
                const token=localStorage.getItem('flowup_token');
                await fetch(`/api/attachments/${id}`,{method:'DELETE',headers:{Authorization:`Bearer ${token}`}});
                setAttachments(prev=>prev.filter(a=>a.id!==id));
              }} style={{padding:'8px 20px',background:'#DC2626',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'700'}}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Comments Modal
// ── DeleteConfirmModal — in-app replacement for window.confirm ──────────
function DeleteConfirmModal({ taskName, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" style={{zIndex:9999}}>
      <div className="modal" style={{width:'380px',textAlign:'center',padding:'32px 28px'}}>
        <div style={{fontSize:'40px',marginBottom:'12px'}}>🗑️</div>
        <h3 style={{fontSize:'16px',fontWeight:'700',color:'var(--text)',marginBottom:'8px'}}>Delete Task?</h3>
        <p style={{fontSize:'13px',color:'var(--muted)',marginBottom:'24px',lineHeight:1.6}}>
          "<strong>{taskName}</strong>" and all its subtasks will be permanently deleted.
        </p>
        <div style={{display:'flex',gap:'10px',justifyContent:'center'}}>
          <button className="btn" onClick={onCancel} style={{padding:'9px 24px'}}>Cancel</button>
          <button onClick={onConfirm}
            style={{padding:'9px 24px',borderRadius:'8px',border:'none',background:'#DC2626',color:'#fff',fontWeight:'700',cursor:'pointer',fontSize:'13px'}}>
            Yes, Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── LogTimeModal — log time directly from task row ──────────────────────────
function LogTimeModal({ task, spaceId, onClose, onSaved }) {
  const [form, setForm] = React.useState({
    date: new Date().toISOString().slice(0,10),
    hours: '',
    unit: 'H',
    notes: ''
  });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  async function save() {
    const h = form.unit === 'D' ? parseFloat(form.hours)*8 : parseFloat(form.hours);
    if (!form.hours || isNaN(h) || h <= 0) { setError('Enter valid hours'); return; }
    if (form.date > new Date().toISOString().slice(0,10)) { setError('Future dates not allowed'); return; }
    setSaving(true);
    setError('');
    try {
      await api.post(`/spaces/${spaceId}/time-entries`, {
        task_id: task.id, date: form.date, hours: h, notes: form.notes
      });
      onSaved();
    } catch(e) {
      setError(e?.error || 'Failed to save');
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{width:'420px'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'16px'}}>
          <div>
            <h3 style={{fontSize:'15px',fontWeight:'700',margin:'0 0 2px'}}>⏱ Log Time</h3>
            <div style={{fontSize:'12px',color:'var(--muted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'320px'}}>{task.name}</div>
          </div>
          <button className="btn btn-sm" onClick={onClose}>✕</button>
        </div>

        {error && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:'8px',padding:'8px 12px',marginBottom:'12px',fontSize:'12px',color:'#DC2626'}}>{error}</div>}

        <div className="form-group">
          <label className="form-label">Date <span style={{color:'#DC2626'}}>*</span></label>
          <input className="input" type="date" value={form.date}
            max={new Date().toISOString().slice(0,10)}
            onChange={e=>setForm(f=>({...f,date:e.target.value}))}/>
          <div style={{fontSize:'10px',color:'var(--muted)',marginTop:'3px'}}>Future dates are not allowed</div>
        </div>

        <div className="form-group">
          <label className="form-label">Time Worked <span style={{color:'#DC2626'}}>*</span></label>
          <div style={{display:'flex',gap:'8px'}}>
            <input className="input" type="number" min="0.1" step="0.5" placeholder="e.g. 2"
              value={form.hours} onChange={e=>setForm(f=>({...f,hours:e.target.value}))} style={{flex:1}}/>
            <select className="select" value={form.unit} onChange={e=>setForm(f=>({...f,unit:e.target.value}))} style={{width:'110px'}}>
              <option value="H">Hours (H)</option>
              <option value="D">Days (D)</option>
            </select>
          </div>
          {form.hours && !isNaN(parseFloat(form.hours)) && (
            <div style={{fontSize:'11px',color:'#5B5BD6',marginTop:'4px',fontWeight:'600'}}>
              = {Math.round((form.unit==='D'?parseFloat(form.hours)*8:parseFloat(form.hours))*10)/10} hours
              {form.unit==='D' && ' (1 Day = 8 Hours)'}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Notes <span style={{fontSize:'10px',fontWeight:'400',color:'var(--muted)'}}>optional</span></label>
          <textarea className="input" rows={2} placeholder="What did you work on?"
            value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}
            style={{resize:'vertical'}}/>
        </div>

        <div style={{display:'flex',gap:'8px',justifyContent:'flex-end',paddingTop:'14px',borderTop:'1px solid var(--border)'}}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Log Time'}
          </button>
        </div>
      </div>
    </div>
  );
}


function CommentsModal({taskId,taskName,comments,newComment,setNewComment,onClose,onPost,onDelete,currentUserId,currentUserRole,users=[]}) {
  const taRef = React.useRef(null);
  const [mentionOpen, setMentionOpen] = React.useState(false);
  const [mentionQuery, setMentionQuery] = React.useState('');
  const [mentionStart, setMentionStart] = React.useState(-1);
  const [mentionIdx, setMentionIdx] = React.useState(0);

  const mentionMatches = mentionOpen ? users.filter(u => {
    const q = mentionQuery.toLowerCase();
    return (u.username||'').toLowerCase().includes(q) || (u.full_name||'').toLowerCase().includes(q);
  }).slice(0, 6) : [];

  function handleChange(e) {
    const val = e.target.value;
    setNewComment(val);
    // Detect an @mention being typed at the caret
    const caret = e.target.selectionStart;
    const upto = val.slice(0, caret);
    const m = upto.match(/@([A-Za-z0-9._-]*)$/);
    if (m) {
      setMentionOpen(true);
      setMentionQuery(m[1]);
      setMentionStart(caret - m[0].length);
      setMentionIdx(0);
    } else {
      setMentionOpen(false);
    }
  }

  function pickMention(u) {
    // Replace the in-progress @query with @username + trailing space
    const before = newComment.slice(0, mentionStart);
    const caret = taRef.current ? taRef.current.selectionStart : newComment.length;
    const after = newComment.slice(caret);
    const inserted = `@${u.username} `;
    const next = before + inserted + after;
    setNewComment(next);
    setMentionOpen(false);
    setTimeout(() => {
      if (taRef.current) {
        const pos = (before + inserted).length;
        taRef.current.focus();
        taRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  }

  function fmtDate(dt) {
    if (!dt) return '';
    const d = new Date(dt);
    return String(d.getDate()).padStart(2,'0')+'/'+String(d.getMonth()+1).padStart(2,'0')+'/'+d.getFullYear()+' '+String(d.getHours()).padStart(2,'0')+':'+String(d.getMinutes()).padStart(2,'0');
  }
  const canDelete = (comment) => comment.user_id===currentUserId || currentUserRole==='superadmin' || currentUserRole==='admin';
  return (
    <div className="modal-overlay">
      <div className="modal" style={{width:'480px',maxHeight:'70vh',display:'flex',flexDirection:'column'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'14px'}}>
          <h3 style={{fontSize:'15px',fontWeight:'700',margin:0}}>💬 Comments
            {comments.length>0&&<span style={{marginLeft:'8px',fontSize:'11px',color:'var(--muted)',fontWeight:'400'}}>({comments.length})</span>}
          </h3>
          <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
            {comments.length>0&&(
              <button onClick={()=>{
                const date=new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'});
                const pw=window.open('','_blank','width=800,height=900');
                if(!pw)return;
                const rows=comments.map(cm=>{
                  const esc=s=>String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
                  const dt=cm.created_at?new Date(cm.created_at).toLocaleString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}):'';
                  const initials=(cm.full_name||'?').split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase();
                  const bg=cm.avatar_color||'#5B5BD6';
                  return `<tr>
                    <td style="padding:14px 16px;vertical-align:top;width:36px">
                      <div style="width:34px;height:34px;border-radius:50%;background:${bg};display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#fff">${initials}</div>
                    </td>
                    <td style="padding:14px 16px;border-bottom:1px solid #f0f0f4">
                      <div style="display:flex;align-items:center;gap:10px;margin-bottom:6px">
                        <span style="font-size:13px;font-weight:700;color:#5B5BD6">${esc(cm.full_name||'Unknown')}</span>
                        <span style="font-size:11px;color:#999">${dt}</span>
                      </div>
                      <div style="font-size:13px;color:#1a1a2e;line-height:1.65;white-space:pre-wrap">${esc(cm.content)}</div>
                    </td>
                  </tr>`;
                }).join('');
                pw.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Comments Export</title>
<style>
  body{font-family:Arial,Helvetica,sans-serif;margin:0;padding:28px;background:#fff;color:#1a1a2e}
  h1{font-size:18px;font-weight:800;margin:0 0 4px;color:#1a1a2e}
  .meta{font-size:11px;color:#888;margin:0 0 6px}
  .task{font-size:13px;font-weight:600;color:#5B5BD6;margin:0 0 20px;padding:10px 14px;background:#f0f0ff;border-radius:8px;border-left:3px solid #5B5BD6}
  table{width:100%;border-collapse:collapse;margin-top:4px}
  tr:nth-child(even) td{background:#fafafa}
  @media print{.no-print{display:none!important}@page{margin:15mm}body{padding:0}}
</style></head><body>
<button class="no-print" onclick="window.print()" style="position:fixed;top:12px;right:12px;padding:8px 18px;background:#5B5BD6;color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:13px;font-weight:700;z-index:999">🖨 Print / Save as PDF</button>
<h1>💬 Task Comments</h1>
<p class="meta">Exported: ${date} &nbsp;·&nbsp; ${comments.length} comment${comments.length!==1?'s':''}</p>
<div class="task">Task: ${taskName||'Unknown Task'}</div>
<table><tbody>${rows}</tbody></table>
</body></html>`);
                pw.document.close();
              }} className="btn btn-sm" style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'11px'}}>
                🖨️ Export PDF
              </button>
            )}
            <button className="btn btn-sm" onClick={onClose}>✕ Close</button>
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto',display:'flex',flexDirection:'column',gap:'10px',marginBottom:'14px'}}>
          {comments.length===0
            ? <p style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'24px'}}>No comments yet — be the first to comment</p>
            : comments.map(cm=>(
                <div key={cm.id} style={{background:'var(--surface2)',borderRadius:'var(--radius-lg)',padding:'10px 14px',border:'1px solid var(--border)'}}>
                  <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'6px'}}>
                    <div style={{width:26,height:26,borderRadius:'50%',background:cm.avatar_color||'#5B5BD6',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'10px',fontWeight:'700',color:'#fff',flexShrink:0}}>
                      {(cm.full_name||'?').split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                    </div>
                    <div style={{flex:1}}>
                      <span style={{fontSize:'12px',fontWeight:'700',color:'var(--brand)'}}>{cm.full_name||'Unknown'}</span>
                      <span style={{fontSize:'10px',color:'var(--muted)',marginLeft:'8px'}}>{fmtDate(cm.created_at)}</span>
                    </div>
                    {canDelete(cm)&&(
                      <button onClick={()=>onDelete(cm.id)}
                        style={{background:'none',border:'none',cursor:'pointer',color:'#DC2626',fontSize:'11px',padding:'2px 6px',borderRadius:'4px',opacity:.7}}
                        title="Delete comment">🗑</button>
                    )}
                  </div>
                  <div style={{fontSize:'13px',color:'var(--text)',lineHeight:1.6,paddingLeft:'34px',whiteSpace:'pre-wrap',wordBreak:'break-word'}}>{cm.content}</div>
                </div>
              ))
          }
        </div>
        <div style={{display:'flex',gap:'8px',borderTop:'1px solid var(--border)',paddingTop:'12px',position:'relative'}}>
          {mentionOpen && mentionMatches.length>0 && (
            <div style={{position:'absolute',bottom:'calc(100% + 4px)',left:0,zIndex:50,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'10px',boxShadow:'0 8px 24px rgba(0,0,0,0.15)',padding:'4px',minWidth:'220px',maxHeight:'200px',overflowY:'auto'}}>
              <div style={{fontSize:'10px',fontWeight:'700',color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.5px',padding:'5px 10px'}}>Mention someone</div>
              {mentionMatches.map((u,i)=>(
                <div key={u.id} onMouseDown={e=>{e.preventDefault();pickMention(u);}}
                  style={{display:'flex',alignItems:'center',gap:'8px',padding:'7px 10px',borderRadius:'6px',cursor:'pointer',background:i===mentionIdx?'var(--surface2)':'transparent'}}
                  onMouseEnter={()=>setMentionIdx(i)}>
                  <div style={{width:24,height:24,borderRadius:'50%',background:u.avatar_color||'#5B5BD6',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'10px',fontWeight:'700',color:'#fff',flexShrink:0}}>
                    {(u.full_name||u.username||'?').split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                  </div>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:'12px',fontWeight:'600',color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{u.full_name||u.username}</div>
                    <div style={{fontSize:'10px',color:'var(--muted)'}}>@{u.username}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <textarea ref={taRef} className="input" value={newComment}
            onChange={handleChange}
            placeholder="Write a comment… use @ to mention someone (Shift+Enter for new line)"
            rows={2}
            style={{flex:1,resize:'vertical',minHeight:'38px',maxHeight:'140px',fontFamily:'inherit',fontSize:'13px',lineHeight:'1.5',padding:'8px 12px'}}
            onKeyDown={e=>{
              if(mentionOpen && mentionMatches.length>0){
                if(e.key==='ArrowDown'){e.preventDefault();setMentionIdx(i=>Math.min(i+1,mentionMatches.length-1));return;}
                if(e.key==='ArrowUp'){e.preventDefault();setMentionIdx(i=>Math.max(i-1,0));return;}
                if(e.key==='Enter'||e.key==='Tab'){e.preventDefault();pickMention(mentionMatches[mentionIdx]);return;}
                if(e.key==='Escape'){setMentionOpen(false);return;}
              }
              if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();onPost();}
            }}/>
          <button className="btn btn-primary btn-sm" onClick={onPost} disabled={!newComment.trim()} style={{alignSelf:'flex-end',flexShrink:0}}>Send</button>
        </div>
      </div>
    </div>
  );
}

// ── Escalation Modal
function EscalationModal({task,users,onClose,onSend}) {
  const pmIds=Array.isArray(task.project_managers)?task.project_managers:[];
  const [form,setForm]=useState({reason:'',impact:'',additional_notes:'',notify_pm_ids:pmIds});
  const [sending,setSending]=useState(false);
  const pmUsers=users.filter(u=>['superadmin','admin','manager'].includes(u.role)||pmIds.includes(u.id));
  const assignees=(task.assignees||[]).map(a=>a.full_name).join(', ')||'—';
  const STATUS_LABELS={not_started:'Not Started',planning:'Planning',in_progress:'In Progress',escalated:'Escalated',at_risk:'At Risk',on_hold:'On Hold',completed:'Completed',cancelled:'Cancelled'};
  async function go(){
    if(!form.reason.trim())return;
    setSending(true);
    try{await onSend(form);}catch(e){setSending(false);}
  }
  return (
    <div className="modal-overlay">
      <div className="modal" style={{width:'560px',maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{display:'flex',gap:'12px',alignItems:'center',marginBottom:'20px',paddingBottom:'14px',borderBottom:'2px solid #FEE2E2'}}>
          <div style={{width:'40px',height:'40px',borderRadius:'10px',background:'rgba(220,38,38,0.1)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'20px',flexShrink:0}}>⚠️</div>
          <div>
            <h3 style={{fontSize:'16px',fontWeight:'700',color:'#DC2626',margin:0}}>Send Escalation Notice</h3>
            <p style={{fontSize:'12px',color:'var(--muted)',margin:'2px 0 0'}}>Task: <strong>{task.name}</strong></p>
          </div>
        </div>

        {/* Task summary */}
        <div style={{background:'var(--surface2)',borderRadius:'var(--radius-lg)',padding:'12px 14px',marginBottom:'16px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
          <div style={{fontSize:'12px'}}><span style={{color:'var(--muted)'}}>Status: </span><strong style={{color:'#DC2626'}}>{STATUS_LABELS[task.status]||task.status}</strong></div>
          <div style={{fontSize:'12px'}}><span style={{color:'var(--muted)'}}>Priority: </span><strong>{task.priority||'Normal'}</strong></div>
          <div style={{fontSize:'12px'}}><span style={{color:'var(--muted)'}}>Start Date: </span><strong>{task.start_date||'Not set'}</strong></div>
          <div style={{fontSize:'12px'}}><span style={{color:'var(--muted)'}}>Due Date: </span><strong>{task.due_date||'Not set'}</strong></div>
          <div style={{fontSize:'12px',gridColumn:'1/3'}}><span style={{color:'var(--muted)'}}>Assignees: </span><strong>{assignees}</strong></div>
        </div>
        <div className="form-group">
          <label className="form-label">Reason for Escalation *</label>
          <textarea className="input" rows={3} value={form.reason} onChange={e=>setForm(f=>({...f,reason:e.target.value}))} placeholder="Describe why this task needs escalation…" style={{resize:'vertical'}}/>
        </div>
        <div className="form-group">
          <label className="form-label">Impact on Project</label>
          <textarea className="input" rows={2} value={form.impact} onChange={e=>setForm(f=>({...f,impact:e.target.value}))} placeholder="What is the impact if unresolved?" style={{resize:'vertical'}}/>
        </div>
        <div className="form-group">
          <label className="form-label">Notify Project Managers</label>
          <div style={{border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'8px',maxHeight:'120px',overflowY:'auto',display:'flex',flexDirection:'column',gap:'6px'}}>
            {pmUsers.map(u=>(
              <label key={u.id} style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer',fontSize:'13px'}}>
                <input type="checkbox" checked={form.notify_pm_ids.includes(u.id)}
                  onChange={()=>setForm(f=>({...f,notify_pm_ids:f.notify_pm_ids.includes(u.id)?f.notify_pm_ids.filter(x=>x!==u.id):[...f.notify_pm_ids,u.id]}))}/>
                <Avatar name={u.full_name} color={u.avatar_color} size={20}/>
                <span style={{flex:1}}>{u.full_name}</span>
                <span style={{fontSize:'11px',color:'var(--muted)'}}>{u.email}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Additional Notes</label>
          <textarea className="input" rows={2} value={form.additional_notes} onChange={e=>setForm(f=>({...f,additional_notes:e.target.value}))} style={{resize:'vertical'}}/>
        </div>
        <div style={{background:'rgba(37,99,235,0.06)',border:'1px solid rgba(37,99,235,0.2)',borderRadius:'var(--radius)',padding:'10px 14px',fontSize:'12px',color:'#1D4ED8',marginBottom:'16px'}}>
          📧 Email will be sent via <strong>SCT.Project@omansat.com</strong> to all selected managers.
        </div>
        <div style={{display:'flex',gap:'10px',justifyContent:'flex-end'}}>
          <button className="btn" onClick={onClose} disabled={sending}>Cancel</button>
          <button style={{padding:'8px 20px',background:'#DC2626',color:'#fff',border:'none',borderRadius:'var(--radius)',fontWeight:'700',cursor:'pointer',fontSize:'13px',display:'flex',alignItems:'center',gap:'8px',minWidth:'140px',justifyContent:'center'}}
            onClick={go} disabled={sending||!form.reason.trim()}>
            {sending?<><div style={{width:12,height:12,border:'2px solid rgba(255,255,255,0.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>Sending…</>:'🚨 Send Escalation'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Un-Complete Modal
function UnCompleteModal({newStatus,currentProgress,onConfirm,onCancel}) {
  const [ded,setDed]=useState(20);
  const np=Math.max(0,currentProgress-ded);
  const pc=np>=60?'#5B5BD6':np>=30?'#D97706':'#DC2626';
  const SL={not_started:'Not Started',planning:'Planning',in_progress:'In Progress',escalated:'Escalated',at_risk:'At Risk',on_hold:'On Hold',cancelled:'Cancelled'};
  return (
    <div className="modal-overlay">
      <div className="modal" style={{width:'450px'}}>
        <div style={{display:'flex',gap:'12px',alignItems:'center',marginBottom:'18px'}}>
          <div style={{width:'40px',height:'40px',borderRadius:'10px',background:'rgba(217,119,6,0.1)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'20px'}}>⚠️</div>
          <div>
            <h3 style={{fontSize:'16px',fontWeight:'700',margin:0}}>Change Completed Status</h3>
            <p style={{fontSize:'12px',color:'var(--muted)',margin:'2px 0 0'}}>Changing to: <strong>{SL[newStatus]||newStatus}</strong></p>
          </div>
        </div>
        <div style={{background:'rgba(217,119,6,0.07)',border:'1px solid rgba(217,119,6,0.25)',borderRadius:'var(--radius-lg)',padding:'12px',marginBottom:'16px',fontSize:'13px',color:'#92400E',lineHeight:1.6}}>
          Current progress is <strong>{Math.round(currentProgress)}%</strong>. How much should be deducted?
        </div>
        <div className="form-group">
          <label className="form-label">Deduction (%)</label>
          <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
            <input type="range" min="0" max={Math.round(currentProgress)} value={ded} onChange={e=>setDed(parseInt(e.target.value)||0)} style={{flex:1,accentColor:'var(--brand)'}}/>
            <input type="number" min="0" max={Math.round(currentProgress)} value={ded} onChange={e=>setDed(Math.min(Math.round(currentProgress),Math.max(0,parseInt(e.target.value)||0)))} className="input" style={{width:'65px',textAlign:'center',fontWeight:'700'}}/>
          </div>
        </div>
        <div style={{background:'var(--surface2)',borderRadius:'var(--radius-lg)',padding:'12px',marginBottom:'18px'}}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px',fontSize:'12px'}}>
            <span style={{color:'var(--muted)'}}>New Progress</span>
            <strong style={{color:pc}}>{Math.round(np)}%</strong>
          </div>
          <div style={{height:8,background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
            <div style={{height:'100%',width:`${np}%`,background:pc,borderRadius:'99px',transition:'width .3s'}}/>
          </div>
        </div>
        <div style={{display:'flex',gap:'10px',justifyContent:'flex-end'}}>
          <button className="btn" onClick={onCancel}>Cancel — Keep Completed</button>
          <button className="btn btn-primary" onClick={()=>onConfirm(ded)}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// ── Inline Assignee Picker (dropdown)
function AssigneePicker({ selected, users, onToggle, onClose }) {
  const [search, setSearch] = React.useState('');
  // Buffer selections locally — only apply on Done
  const [pending, setPending] = React.useState(selected || []);
  const filtered = users.filter(u => u.full_name.toLowerCase().includes(search.toLowerCase()));

  function toggle(uid) {
    setPending(p => p.includes(uid) ? p.filter(x=>x!==uid) : [...p, uid]);
  }

  function handleDone() {
    // Find what changed and call onToggle for each difference
    const added = pending.filter(id => !(selected||[]).includes(id));
    const removed = (selected||[]).filter(id => !pending.includes(id));
    // Call onToggle with final list
    onToggle(pending);
    onClose();
  }

  return (
    <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,zIndex:300,
      background:'var(--surface)',border:'1px solid var(--border)',
      borderRadius:'12px',boxShadow:'0 12px 40px rgba(0,0,0,0.18)',
      minWidth:'280px',width:'300px',padding:'8px',
      maxHeight:'340px',display:'flex',flexDirection:'column'}}>
      {/* Search box */}
      <div style={{padding:'0 2px 6px'}}>
        <input autoFocus className="input" type="text"
          placeholder="Search people…" value={search}
          onChange={e=>setSearch(e.target.value)}
          style={{width:'100%',height:'32px',fontSize:'13px'}}/>
      </div>
      {/* User list */}
      <div style={{overflowY:'auto',flex:1}}>
        {filtered.length===0
          ? <div style={{padding:'12px',textAlign:'center',fontSize:'12px',color:'var(--muted)'}}>No users found</div>
          : filtered.map(u => {
              const isSel = pending.includes(u.id);
              return (
                <div key={u.id} onClick={()=>toggle(u.id)}
                  style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 10px',
                    borderRadius:'8px',cursor:'pointer',
                    background:isSel?'var(--brand-dim)':'transparent',transition:'background .1s'}}
                  onMouseEnter={e=>{if(!isSel)e.currentTarget.style.background='var(--surface2)';}}
                  onMouseLeave={e=>{e.currentTarget.style.background=isSel?'var(--brand-dim)':'transparent';}}>
                  <div style={{width:16,height:16,borderRadius:'4px',
                    border:`2px solid ${isSel?'var(--brand)':'var(--border)'}`,
                    background:isSel?'var(--brand)':'transparent',
                    display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    {isSel&&<span style={{color:'#fff',fontSize:'10px',lineHeight:1,fontWeight:'900'}}>✓</span>}
                  </div>
                  <Avatar name={u.full_name} color={u.avatar_color} size={26}/>
                  <span style={{fontSize:'13px',fontWeight:isSel?'700':'400',color:'var(--text)',flex:1}}>{u.full_name}</span>
                </div>
              );
            })
        }
      </div>
      {/* Footer */}
      <div style={{borderTop:'1px solid var(--border)',paddingTop:'8px',marginTop:'4px',
        display:'flex',justifyContent:'space-between',alignItems:'center',gap:'8px'}}>
        <span style={{fontSize:'11px',color:'var(--muted)'}}>{pending.length} selected</span>
        <div style={{display:'flex',gap:'6px'}}>
          <button className="btn btn-sm" onClick={onClose}
            style={{fontSize:'11px',padding:'4px 12px'}}>Cancel</button>
          <button className="btn btn-sm btn-primary" onClick={handleDone}
            style={{fontSize:'11px',padding:'4px 14px'}}>✓ Done</button>
        </div>
      </div>
    </div>
  );
}

// ── Weight Distribution Modal
// Shows when adding a new task/subtask would push total weight above 100

function WeightDistributionModal({ siblings, newWeight, newTaskName, onConfirm, onCancel }) {
  const currentTotal = siblings.reduce((s,t)=>s+(t.task_weight!=null?parseFloat(t.task_weight):0),0);
  const excess = currentTotal + newWeight - 100;

  // Initialize with CURRENT weights unchanged — let user decide where to reduce
  const [adjustments, setAdjustments] = useState(() =>
    siblings.map(s => ({ ...s, newWeight: s.task_weight!=null?parseFloat(s.task_weight):0 }))
  );
  const [mode, setMode] = useState('manual'); // start in manual so user picks

  function applyProportional() {
    const adj = siblings.map(s => ({
      ...s,
      newWeight: Math.max(0, Math.round((s.task_weight!=null?parseFloat(s.task_weight):0) - (excess * (s.task_weight!=null?parseFloat(s.task_weight):0) / (currentTotal||1))))
    }));
    setAdjustments(adj);
    setMode('proportional');
  }

  function applyEqual() {
    const total = 100 - newWeight;
    const each = Math.max(0, Math.floor(total / siblings.length));
    const adj = siblings.map((s,i) => ({
      ...s,
      newWeight: i === siblings.length-1 ? Math.max(0, total - each*(siblings.length-1)) : each
    }));
    setAdjustments(adj);
    setMode('equal');
  }

  function updateManual(id, val) {
    setAdjustments(prev => prev.map(s => s.id===id ? {...s, newWeight: Math.max(0,parseInt(val)||0)} : s));
    setMode('manual');
  }

  const newTotal = adjustments.reduce((s,a)=>s+a.newWeight,0) + newWeight;
  const isValid = newTotal <= 100;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{width:'520px'}}>
        {/* Header */}
        <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'20px',paddingBottom:'14px',borderBottom:'1px solid var(--border)'}}>
          <div style={{width:'40px',height:'40px',borderRadius:'10px',background:'rgba(217,119,6,0.1)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'20px',flexShrink:0}}>⚖️</div>
          <div>
            <h3 style={{fontSize:'16px',fontWeight:'700',margin:0}}>Weight Distribution</h3>
            <p style={{fontSize:'12px',color:'var(--muted)',margin:'2px 0 0'}}>
              Adding <strong>"{newTaskName}"</strong> (weight: {newWeight}) would exceed 100%. Adjust existing weights.
            </p>
          </div>
        </div>

        {/* Info bar */}
        <div style={{background:'rgba(217,119,6,0.07)',border:'1px solid rgba(217,119,6,0.25)',borderRadius:'var(--radius-lg)',padding:'10px 14px',marginBottom:'16px',fontSize:'12px',color:'#92400E'}}>
          Current total: <strong>{currentTotal}</strong> + New task: <strong>{newWeight}</strong> = <strong style={{color:'#DC2626'}}>{currentTotal+newWeight}</strong> (exceeds 100 by {excess})
        </div>

        {/* Distribution mode buttons */}
        <div style={{display:'flex',gap:'8px',marginBottom:'16px'}}>
          <button onClick={applyProportional}
            className={`btn btn-sm ${mode==='proportional'?'btn-primary':''}`}>
            ⚖️ Proportional
          </button>
          <button onClick={applyEqual}
            className={`btn btn-sm ${mode==='equal'?'btn-primary':''}`}>
            ═ Equal Split
          </button>
          <button onClick={()=>setMode('manual')}
            className={`btn btn-sm ${mode==='manual'?'btn-primary':''}`}>
            ✏️ Manual
          </button>
          <div style={{marginLeft:'auto',fontSize:'12px',color:'var(--muted)',display:'flex',alignItems:'center',gap:'4px'}}>
            New total: <strong style={{color:isValid?'#16A34A':'#DC2626'}}>{newTotal}</strong>/100
          </div>
        </div>

        {/* Existing tasks */}
        <div style={{border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',overflow:'hidden',marginBottom:'16px'}}>
          <div style={{background:'var(--surface2)',padding:'8px 14px',fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted)',display:'grid',gridTemplateColumns:'1fr 80px 80px'}}>
            <span>Task</span><span style={{textAlign:'center'}}>Current</span><span style={{textAlign:'center'}}>New Weight</span>
          </div>
          {adjustments.map((s,i)=>(
            <div key={s.id} style={{display:'grid',gridTemplateColumns:'1fr 80px 80px',borderTop:'1px solid var(--border)',alignItems:'center',padding:'8px 14px',background:i%2===0?'transparent':'rgba(91,91,214,0.02)'}}>
              <span style={{fontSize:'13px',fontWeight:'500',color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.name}</span>
              <span style={{fontSize:'12px',color:'var(--muted)',textAlign:'center'}}>{s.task_weight!=null?s.task_weight:0}</span>
              <div style={{display:'flex',alignItems:'center',justifyContent:'center'}}>
                <input type="number" min="0" max="100" value={s.newWeight}
                  onChange={e=>updateManual(s.id,e.target.value)}
                  style={{width:'55px',height:'28px',border:`1.5px solid ${s.newWeight<(s.task_weight!=null?parseFloat(s.task_weight):0)?'#D97706':'var(--border)'}`,borderRadius:'6px',background:'var(--surface)',color:'var(--text)',textAlign:'center',fontSize:'12px',fontWeight:'700',outline:'none',padding:'0 4px'}}/>
              </div>
            </div>
          ))}
          {/* New task row */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 80px 80px',borderTop:'2px solid var(--brand)',alignItems:'center',padding:'8px 14px',background:'var(--brand-dim)'}}>
            <span style={{fontSize:'13px',fontWeight:'600',color:'var(--brand)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>+ {newTaskName} <span style={{fontSize:'10px',color:'var(--muted)',fontWeight:'400'}}>(new)</span></span>
            <span style={{fontSize:'12px',color:'var(--muted)',textAlign:'center'}}>—</span>
            <span style={{fontSize:'13px',fontWeight:'800',color:'var(--brand)',textAlign:'center'}}>{newWeight}</span>
          </div>
        </div>

        {/* Total bar */}
        <div style={{marginBottom:'16px'}}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px',fontSize:'12px'}}>
            <span style={{color:'var(--muted)'}}>Total weight</span>
            <strong style={{color:isValid?'#16A34A':'#DC2626'}}>{newTotal} / 100 {isValid?'✓':'⚠️'}</strong>
          </div>
          <div style={{height:8,background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
            <div style={{height:'100%',width:`${Math.min(100,newTotal)}%`,background:isValid?'#16A34A':'#DC2626',borderRadius:'99px',transition:'width .3s'}}/>
          </div>
          {!isValid&&<p style={{fontSize:'11px',color:'#DC2626',marginTop:'4px'}}>Total exceeds 100. Please reduce some weights.</p>}
        </div>

        <div style={{display:'flex',gap:'10px',justifyContent:'flex-end'}}>
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={()=>onConfirm(adjustments)} disabled={!isValid}>
            Confirm & Save
          </button>
        </div>
      </div>
    </div>
  );
}